export type LandingCatalogPageKey =
  | 'features'
  | 'emr-ehr'
  | 'clinical-ai'
  | 'patient-portal'
  | 'calendar-booking'
  | 'billing-payments'
  | 'multi-site-operations'
  | 'reports-insights'
  | 'marketing-growth'
  | 'who-we-serve'
  | 'clinic-owners'
  | 'practice-managers'
  | 'practitioners'
  | 'solo-practices'
  | 'growing-teams'
  | 'multi-location-groups'
  | 'primary-care-urgent-care'
  | 'aesthetics-wellness'
  | 'mental-health-therapy'
  | 'wellness-preventive-care'
  | 'medical-aesthetics'
  | 'womens-mens-health'
  | 'musculoskeletal-performance'
  | 'mental-health-lifestyle'
  | 'medspa'
  | 'skin-clinic'
  | 'aesthetic-clinic'
  | 'laser-clinic'
  | 'dermatology'
  | 'hair-transplant-clinic'
  | 'cosmetic-surgery'
  | 'spa'
  | 'fertility'
  | 'sexual-health'
  | 'ivf-gynecology'
  | 'pelvic-health'
  | 'mens-health'
  | 'hormone-replacement-therapy'
  | 'physical-therapy'
  | 'osteopathy'
  | 'sports-medicine'
  | 'regenerative-medicine'
  | 'chiropractic'
  | 'prp-therapy'
  | 'mental-health'
  | 'psychiatry'
  | 'psychology'
  | 'coaching'
  | 'therapy-counseling'
  | 'speech-therapy'
  | 'adhd-assessment'
  | 'occupational-therapy'
  | 'practice-owner'
  | 'treatment-providers'
  | 'front-of-house-staff'
  | 'marketers'
  | 'enterprise'
  | 'large-practice'
  | 'small-practice'
  | 'solo-practitioner'
  | 'all-specialities'
  | 'why-elen1'
  | 'case-studies'
  | 'security'
  | 'comparisons'
  | 'resources'
  | 'help-center'
  | 'developer-api'
  | 'guides'
  | 'pricing'
  | 'book-demo';

export type LandingCatalogSection = {
  title: string;
  body: string;
  bullets: string[];
};

export type LandingCatalogCapability = {
  title: string;
  body: string;
};

export type LandingCatalogScreenshot = {
  src: string;
  alt: string;
  title: string;
  body: string;
};

export type LandingCatalogCta = {
  eyebrow: string;
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel: string;
  proofs: string[];
};

export type LandingCatalogFaqItem = {
  question: string;
  answer: string;
};

export type LandingCatalogPage = {
  path: string;
  navGroup: 'features' | 'who-we-serve' | 'why-us' | 'resources' | 'pricing' | 'cta';
  eyebrow: string;
  title: string;
  subtitle: string;
  highlights: string[];
  sections: LandingCatalogSection[];
  capabilities?: LandingCatalogCapability[];
  screenshots?: LandingCatalogScreenshot[];
  cta?: LandingCatalogCta;
  faq?: LandingCatalogFaqItem[];
  relatedDescriptions?: Partial<Record<LandingCatalogPageKey, string>>;
  related: LandingCatalogPageKey[];
};

export type LandingMenuCard = {
  key: LandingCatalogPageKey;
  icon: string;
  label: string;
  description: string;
};

export type LandingMenuGroup = {
  title: string;
  items: LandingMenuCard[];
};

export type LandingTopNav = {
  label: string;
  key: LandingCatalogPageKey;
  groups?: LandingMenuGroup[];
  aside?: {
    eyebrow: string;
    title: string;
    body: string;
    ctaLabel: string;
    ctaKey: LandingCatalogPageKey;
  };
};

const solutionHighlights = ['Specialty-ready workflows', 'Connected booking, charting, billing, and follow-up', 'Operational visibility for growing clinics'];

function createSolutionPage(
  key: LandingCatalogPageKey,
  label: string,
  eyebrow: string,
  positioning: string,
  workflowFocus: string,
): LandingCatalogPage {
  const path = `/${key}`;
  return {
    path,
    navGroup: 'who-we-serve',
    eyebrow,
    title: `Elen1 for ${label}`,
    subtitle: `${positioning} Elen1 brings scheduling, intake, clinical work, payments, patient communication, and reporting into one calmer operating system.`,
    highlights: solutionHighlights,
    sections: [
      {
        title: `Run ${label.toLowerCase()} workflows without stitching tools together`,
        body: `${workflowFocus} The team can manage bookings, forms, notes, payments, follow-up, and patient context from one shared workspace instead of moving between disconnected systems.`,
        bullets: [
          'Keep appointment, patient, billing, and communication context connected',
          'Give front desk, providers, and managers a shared view of the work',
          'Reduce duplicated admin steps across intake, treatment, payment, and follow-up',
        ],
      },
      {
        title: 'Support growth with clearer operations',
        body: 'As volume, services, or locations grow, Elen1 helps standardize the daily workflow while preserving the flexibility each clinic model needs.',
        bullets: [
          'Track demand, utilization, revenue, and follow-up from one reporting layer',
          'Use role-based workflows so each team sees the work that matters to them',
          'Create a cleaner demo and onboarding path for this clinic type',
        ],
      },
    ],
    capabilities: [
      {
        title: 'Cleaner patient flow',
        body: 'Keep booking, intake, visit status, documentation, payment, and next steps connected so the patient journey is easier to manage.',
      },
      {
        title: 'Less operational drag',
        body: 'Reduce side spreadsheets, duplicate entry, and disconnected handoffs between reception, providers, billing, and management.',
      },
      {
        title: 'Better visibility',
        body: 'See performance, workload, and follow-up signals in one system so teams can act earlier and run the clinic with more control.',
      },
    ],
    cta: {
      eyebrow,
      title: `See how Elen1 fits ${label.toLowerCase()}.`,
      body: 'Book a focused walkthrough and we will map the platform to your services, team structure, patient journey, and reporting needs.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Scheduling, intake, charting, billing, and follow-up in one workflow',
        'Role-aware views for owners, managers, providers, and front desk teams',
        'Reporting that connects patient flow, revenue, and team execution',
      ],
    },
    related: ['who-we-serve', 'features', 'calendar-booking', 'book-demo'],
  };
}

export const SOLUTIONS_SPECIALTY_GROUPS = [
  {
    title: 'Wellness and Preventive Care',
    categoryKey: 'wellness-preventive-care',
    icon: 'ph-flower-lotus',
    banner: 'Preventive and ongoing care workflows for wellness-focused clinics',
    items: [
      { key: 'wellness-preventive-care', icon: 'ph-flower-lotus', label: 'Wellness and Preventive Care', description: 'Preventive care, membership, follow-up, and patient engagement workflows.' },
    ],
  },
  {
    title: 'Medical Aesthetics',
    categoryKey: 'medical-aesthetics',
    icon: 'ph-face-mask',
    banner: 'Surgical and non-surgical aesthetic care',
    items: [
      { key: 'medspa', icon: 'ph-flower', label: 'Medspa', description: 'Consults, packages, payments, memberships, and recurring treatment plans.' },
      { key: 'skin-clinic', icon: 'ph-head-circuit', label: 'Skin Clinic', description: 'Skin health visits, treatment plans, photos, products, and follow-up.' },
      { key: 'aesthetic-clinic', icon: 'ph-calendar-plus', label: 'Aesthetic Clinic', description: 'Consult-to-treatment workflows, forms, consent, billing, and rebooking.' },
      { key: 'laser-clinic', icon: 'ph-syringe', label: 'Laser Clinic', description: 'Treatment courses, device-room scheduling, deposits, and aftercare.' },
      { key: 'dermatology', icon: 'ph-fingerprint', label: 'Dermatology', description: 'Clinical skin workflows, documentation, procedures, and patient follow-up.' },
      { key: 'hair-transplant-clinic', icon: 'ph-plant', label: 'Hair Transplant Clinic', description: 'Consults, surgical planning, payments, post-op tasks, and long follow-up arcs.' },
      { key: 'cosmetic-surgery', icon: 'ph-pen-nib', label: 'Cosmetic Surgery', description: 'Surgical consults, pre-op forms, quotes, payments, and recovery follow-up.' },
      { key: 'spa', icon: 'ph-stack-simple', label: 'Spa', description: 'Bookings, memberships, retail, packages, deposits, and repeat visits.' },
    ],
  },
  {
    title: "Women's and Men's Health",
    categoryKey: 'womens-mens-health',
    icon: 'ph-gender-intersex',
    banner: 'Specialist care for reproductive, hormonal, and sexual health',
    items: [
      { key: 'fertility', icon: 'ph-key', label: 'Fertility', description: 'Longitudinal care plans, cycle tracking, labs, communication, and follow-up.' },
      { key: 'sexual-health', icon: 'ph-gender-intersex', label: 'Sexual Health', description: 'Confidential intake, testing workflows, results, messages, and recalls.' },
      { key: 'ivf-gynecology', icon: 'ph-syringe', label: 'IVF & Gynecology', description: 'Appointments, lab follow-up, treatment cycles, forms, and secure messaging.' },
      { key: 'pelvic-health', icon: 'ph-shield-checkered', label: 'Pelvic Health', description: 'Care plans, recurring appointments, outcomes, and interdisciplinary follow-up.' },
      { key: 'mens-health', icon: 'ph-gender-male', label: "Men's Health", description: 'Consults, labs, treatment plans, prescriptions, and follow-up reminders.' },
      { key: 'hormone-replacement-therapy', icon: 'ph-gender-intersex', label: 'Hormone Replacement Therapy', description: 'Labs, dosing follow-up, recurring care, communication, and reporting.' },
    ],
  },
  {
    title: 'Musculoskeletal and Performance',
    categoryKey: 'musculoskeletal-performance',
    icon: 'ph-first-aid-kit',
    banner: 'Non-surgical and regenerative care for movement and recovery',
    items: [
      { key: 'physical-therapy', icon: 'ph-person-simple-bike', label: 'Physical Therapy', description: 'Recurring visits, exercise plans, progress tracking, and follow-up.' },
      { key: 'osteopathy', icon: 'ph-barbell', label: 'Osteopathy', description: 'Hands-on care workflows, care plans, charting, and rebooking.' },
      { key: 'sports-medicine', icon: 'ph-barbell', label: 'Sports Medicine', description: 'Injury assessment, rehab coordination, referrals, and performance follow-up.' },
      { key: 'regenerative-medicine', icon: 'ph-first-aid', label: 'Regenerative Medicine', description: 'Consults, procedures, consent, outcomes tracking, and follow-up.' },
      { key: 'chiropractic', icon: 'ph-bone', label: 'Chiropractic', description: 'Recurring care plans, packages, documentation, and appointment flow.' },
      { key: 'prp-therapy', icon: 'ph-syringe', label: 'PRP Therapy', description: 'Procedure scheduling, consent, payments, post-treatment checks, and outcomes.' },
    ],
  },
  {
    title: 'Mental Health and Lifestyle',
    categoryKey: 'mental-health-lifestyle',
    icon: 'ph-brain',
    banner: 'Clinical mental health and behavioral care',
    items: [
      { key: 'mental-health', icon: 'ph-brain', label: 'Mental Health', description: 'Recurring care, secure messages, notes, tasks, and follow-through.' },
      { key: 'psychiatry', icon: 'ph-head-circuit', label: 'Psychiatry', description: 'Medication follow-up, labs, documentation, care plans, and communication.' },
      { key: 'psychology', icon: 'ph-chart-line-up', label: 'Psychology', description: 'Assessments, longitudinal notes, recurring sessions, and outcomes.' },
      { key: 'coaching', icon: 'ph-user-check', label: 'Coaching', description: 'Programs, recurring sessions, goals, reminders, and client engagement.' },
      { key: 'therapy-counseling', icon: 'ph-chat-teardrop-text', label: 'Therapy & Counseling', description: 'Session notes, secure communication, scheduling, and care continuity.' },
      { key: 'speech-therapy', icon: 'ph-ear', label: 'Speech Therapy', description: 'Recurring therapy, progress tracking, exercises, and family communication.' },
      { key: 'adhd-assessment', icon: 'ph-head-circuit', label: 'ADHD Assessment', description: 'Questionnaires, assessments, reports, appointments, and follow-up.' },
      { key: 'occupational-therapy', icon: 'ph-heart', label: 'Occupational Therapy', description: 'Care plans, progress tracking, recurring therapy, and patient tasks.' },
    ],
  },
] as const;

export const SOLUTIONS_ROLE_ITEMS = [
  { key: 'practice-owner', label: 'Practice Owner' },
  { key: 'practice-managers', label: 'Practice Managers' },
  { key: 'treatment-providers', label: 'Treatment Providers' },
  { key: 'front-of-house-staff', label: 'Front of House Staff' },
  { key: 'marketers', label: 'Marketers' },
] as const;

export const SOLUTIONS_BUSINESS_SIZE_ITEMS = [
  { key: 'enterprise', label: 'Enterprise' },
  { key: 'large-practice', label: 'Large practice' },
  { key: 'small-practice', label: 'Small practice' },
  { key: 'solo-practitioner', label: 'Solo practitioner' },
] as const;

export const SOLUTIONS_OTHER_ITEMS = [
  { key: 'all-specialities', label: 'All specialities' },
] as const;

const solutionPageDefinitions = [
  ['wellness-preventive-care', 'Wellness and Preventive Care', 'Specialty', 'Preventive and wellness clinics need smooth recurring care, membership, follow-up, and patient engagement workflows.', 'Wellness teams often manage a steady rhythm of recurring visits, reminders, care plans, and patient communication.'],
  ['medical-aesthetics', 'Medical Aesthetics', 'Specialty', 'Aesthetics clinics need a polished workflow from consultation to treatment, payment, and return visit.', 'Medical aesthetics teams need consults, consent, treatment plans, deposits, packages, photos, and follow-up to stay connected.'],
  ['womens-mens-health', "Women's and Men's Health", 'Specialty', 'Specialist health clinics need discreet, longitudinal workflows for reproductive, hormonal, and sexual health.', 'These teams need intake, labs, recurring visits, secure messaging, treatment plans, and follow-up to stay coordinated.'],
  ['musculoskeletal-performance', 'Musculoskeletal and Performance', 'Specialty', 'Movement, recovery, and performance clinics need recurring care workflows that keep progress visible.', 'These clinics coordinate assessments, care plans, procedures, therapy, outcomes, and repeat visits across longer recovery arcs.'],
  ['mental-health-lifestyle', 'Mental Health and Lifestyle', 'Specialty', 'Mental health and lifestyle teams need continuity, secure communication, and recurring-care structure.', 'These practices rely on recurring visits, private notes, reminders, programs, and ongoing patient engagement.'],
  ['practice-owner', 'Practice Owners', 'Role', 'Owners need clear visibility across revenue, utilization, patient flow, and team execution.', 'Practice owners need fewer blind spots across appointments, payments, reporting, staffing, and growth.'],
  ['treatment-providers', 'Treatment Providers', 'Role', 'Providers need patient context, documentation, and follow-up tools that do not slow down care.', 'Treatment providers need to review context, document care, assign next steps, and keep the patient journey moving.'],
  ['front-of-house-staff', 'Front of House Staff', 'Role', 'Front desk teams need booking, intake, payments, and patient communication in one simple workflow.', 'Front-of-house staff carry the handoffs between patients, providers, payments, reminders, and daily schedule changes.'],
  ['marketers', 'Marketers', 'Role', 'Clinic marketers need campaign, recall, review, and reactivation workflows tied to real patient activity.', 'Marketing teams need outreach that connects to appointments, services, retention, reviews, and revenue instead of a separate list.'],
  ['enterprise', 'Enterprise Clinics', 'Business size', 'Enterprise healthcare groups need governance, reporting, permissions, and workflow consistency across teams and sites.', 'Enterprise teams need standardized controls and visibility without removing local operating flexibility.'],
  ['large-practice', 'Large Practices', 'Business size', 'Large practices need stronger coordination across providers, rooms, locations, and revenue workflows.', 'Large practices need daily operations, reporting, scheduling, billing, and patient experience to work from the same system.'],
  ['small-practice', 'Small Practices', 'Business size', 'Small practices need a connected system that replaces admin sprawl without adding complexity.', 'Small practices need booking, records, payments, reminders, and reporting without maintaining a fragmented software stack.'],
  ['solo-practitioner', 'Solo Practitioners', 'Business size', 'Solo practitioners need lean software that handles the admin load without creating more work.', 'Solo providers need simple scheduling, notes, payments, patient communication, and follow-up in one place.'],
  ['all-specialities', 'All Specialities', 'Specialty', 'Elen1 adapts across care models, specialties, business sizes, and team structures.', 'Clinics across specialties need the same foundation: connected scheduling, records, payments, communication, and reporting.'],
] as const;

for (const group of SOLUTIONS_SPECIALTY_GROUPS) {
  for (const item of group.items) {
    if (!solutionPageDefinitions.some(([key]) => key === item.key)) {
      (solutionPageDefinitions as unknown as Array<readonly [LandingCatalogPageKey, string, string, string, string]>).push([
        item.key,
        item.label,
        'Specialty',
        item.description,
        item.description,
      ]);
    }
  }
}

const SOLUTION_CATALOG_PAGES = Object.fromEntries(
  solutionPageDefinitions.map(([key, label, eyebrow, positioning, workflowFocus]) => [
    key,
    createSolutionPage(key, label, eyebrow, positioning, workflowFocus),
  ]),
) as Partial<Record<LandingCatalogPageKey, LandingCatalogPage>>;

/* Asserted rather than annotated: the specialty pages spread in from
   SOLUTION_CATALOG_PAGES is Partial to the type system, but the build loop above
   guarantees every specialty key exists at runtime. */
export const LANDING_CATALOG_PAGES = {
  'features': {
    path: '/features',
    navGroup: 'features',
    eyebrow: 'Platform overview',
    title: 'One system to run the clinic without stitching everything together.',
    subtitle: 'Elen1 keeps records, scheduling, billing, reporting, and patient communication connected so teams can get through the day without bouncing between separate tools.',
    highlights: ['One place for the full clinic workflow', 'Shared context from booking to billing', 'Built to grow with the clinic'],
    sections: [],
    capabilities: [
      {
        title: 'Keep the whole clinic on one system',
        body: 'Patient records, scheduling, billing, communication, and reporting stay connected so staff are not constantly translating work from one tool into another.',
      },
      {
        title: 'Give every team the same operating picture',
        body: 'Leadership, managers, front desk staff, and clinicians can work from the same underlying data instead of carrying separate dashboards and side reports.',
      },
      {
        title: 'Grow without adding more system sprawl',
        body: 'Add providers, workflows, or locations on top of a stable operating model instead of layering new software onto old process problems.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Unified operations dashboard with clinical and operational intelligence',
        title: 'A clear view of how the clinic is really running',
        body: 'See appointments, patient activity, workload, and performance in one place instead of piecing the story together from multiple dashboards.',
      },
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Patient portal appointments experience showing upcoming visits and self-service actions',
        title: 'Scheduling that does not live in its own silo',
        body: 'Booking, reminders, self-service actions, and follow-up stay tied to the same workflow the team uses every day.',
      },
      {
        src: '/landing-captures/billing-standalone.png',
        alt: 'Billing workspace with invoice builder and outstanding totals',
        title: 'Billing that stays close to the visit',
        body: 'Charges, balances, and collection work stay connected to the patient and appointment instead of drifting into a separate process.',
      },
      {
        src: '/landing-captures/reports-standalone.png',
        alt: 'Custom report builder for operational and leadership reporting',
        title: 'Reporting built from the same work the team is already doing',
        body: 'Operational and leadership reporting comes from the live workflow data, so teams can trust what they are looking at and act on it faster.',
      },
    ],
    related: ['emr-ehr', 'clinical-ai', 'calendar-booking', 'billing-payments', 'reports-insights', 'pricing'],
  },
  'emr-ehr': {
    path: '/emr-ehr',
    navGroup: 'features',
    eyebrow: 'Care & clinical',
    title: 'AI EMR & EHR',
    subtitle: 'Start the visit with the full patient picture, document faster, and keep follow-up tied to the chart.',
    highlights: ['Patient story in one place', 'Notes that do not slow the visit', 'Clear next steps after every encounter'],
    sections: [
      {
        title: 'Start the visit with context, not detective work',
        body: 'Clinicians should not have to dig through tabs, old notes, and scattered messages to understand what is going on. Elen1 keeps history, medications, forms, recent activity, and open tasks close to the chart so the visit can start with a clearer picture.',
        bullets: ['Review the patient timeline, forms, tasks, and prior activity in one place', 'Use reusable chart templates without forcing every visit into rigid documentation', 'Keep the same patient context visible for both clinicians and supporting staff'],
      },
      {
        title: 'Make the chart useful after the visit ends',
        body: 'The record should help the team move care forward, not just store what happened. Elen1 keeps plans, unresolved tasks, communication, and follow-up visible so the next step is easier to carry out.',
        bullets: ['Keep care gaps, pending work, and next actions visible after the encounter', 'See communication history without leaving the patient workflow', 'Give both the clinic team and the patient a clearer sense of what happens next'],
      },
    ],
    related: ['clinical-ai', 'patient-portal', 'calendar-booking', 'reports-insights'],
  },
  'clinical-ai': {
    path: '/clinical-ai',
    navGroup: 'features',
    eyebrow: 'Care & clinical',
    title: 'Clinical AI',
    subtitle: 'Use AI where it actually helps: summarizing intake, preparing clinicians, and speeding up follow-up without creating another tool to manage.',
    highlights: ['Summaries where the team already works', 'Triage support that stays reviewable', 'Cleaner handoffs between intake and care'],
    sections: [
      {
        title: 'Spend less time rewriting what the system already knows',
        body: 'Clinical AI helps turn intake, calls, and visit context into summaries the team can actually use, instead of creating more raw notes that still need to be pieced together manually.',
        bullets: ['Generate encounter and communication summaries inside the live workflow', 'Surface follow-up suggestions that give staff a stronger starting point', 'Highlight risks, missing context, and next actions without making the user hunt for them'],
      },
      {
        title: 'Keep AI inside a workflow people can trust',
        body: 'AI is most useful when it stays inside the chart and the operating workflow, not in a separate assistant window. Elen1 keeps review, accountability, and execution close together so the output can actually be used.',
        bullets: ['Review AI output before it becomes part of the record or workflow', 'Show the clinical and operational context beside the AI suggestion', 'Keep ownership clear across intake, care delivery, and follow-up'],
      },
    ],
    capabilities: [
      {
        title: 'AI that shows up where work is already happening',
        body: 'Summaries, prompts, and risk signals appear inside the workflow the team already uses instead of being buried in a separate assistant experience.',
      },
      {
        title: 'Review before action',
        body: 'Teams can check outputs, keep accountability clear, and use AI to support decisions without blurring who owns the final call.',
      },
      {
        title: 'Less re-reading across roles',
        body: 'Intake, clinicians, and follow-up teams can work from the same synthesized context instead of rewriting or re-reading the same story from scratch.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Sentinel AI agent embedded in the EMR workflow',
        title: 'Decision support inside the chart',
        body: 'Recommendations and summaries appear in the same place clinicians already review the record, which makes the AI easier to use and easier to govern.',
      },
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Automatic triage AI prioritizing incoming patients by risk and urgency',
        title: 'Triage support when intake volume picks up',
        body: 'AI can help classify incoming demand, surface risk, and direct attention faster when the team is dealing with a busy intake flow.',
      },
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Patient details view with AI-generated longitudinal summary and care gaps',
        title: 'Patient summaries that save real time',
        body: 'Use the patient snapshot to get oriented around history, open risks, and unresolved follow-up without rebuilding context manually before every visit.',
      },
    ],
    related: ['emr-ehr', 'primary-care-urgent-care', 'mental-health-therapy', 'case-studies'],
  },
  'patient-portal': {
    path: '/patient-portal',
    navGroup: 'features',
    eyebrow: 'Care & clinical',
    title: 'Patient portal',
    subtitle: 'Give patients one place to complete forms, message the clinic, manage appointments, and stay on top of what happens next.',
    highlights: ['More self-service, fewer phone calls', 'Secure messaging in context', 'Clearer follow-through between visits'],
    sections: [
      {
        title: 'Let patients handle the routine work themselves',
        body: 'Patients should not need to call the clinic for every form, update, or appointment question. Elen1 gives them a clearer self-service experience for the tasks that happen before and after care.',
        bullets: ['Show appointments, care tasks, and next steps in one place', 'Collect pre-visit and follow-up forms without manual chasing', 'Make messages, instructions, and updates easier to find'],
      },
      {
        title: 'Keep the relationship going between visits',
        body: 'The portal helps care continue after the encounter by making tasks, documents, instructions, and updates visible in a simple, familiar flow for patients.',
        bullets: ['Share results and care-plan updates without extra back-and-forth', 'Give patients billing visibility when it is relevant', 'Reduce the admin burden that usually lands on the front desk'],
      },
    ],
    related: ['calendar-booking', 'billing-payments', 'help-center', 'book-demo'],
  },
  'calendar-booking': {
    path: '/calendar-booking',
    navGroup: 'features',
    eyebrow: 'Scheduling',
    title: 'Calendar & booking',
    subtitle: 'Keep schedules, rooms, reminders, waitlists, and telehealth in one booking workflow the whole clinic can trust.',
    highlights: ['Schedules everyone can rely on', 'Fewer surprises on the day', 'Rooms, waitlists, and telehealth in sync'],
    sections: [
      {
        title: 'Book appointments in a way the clinic can actually deliver',
        body: 'Scheduling is not just filling empty slots. It is about matching provider availability, visit type, room capacity, location, and patient readiness so the day runs with fewer surprises.',
        bullets: ['Keep shared calendars visible across teams and locations', 'Use waitlists and overflow handling without creating manual juggling', 'Book around room and equipment constraints instead of fixing conflicts later'],
      },
      {
        title: 'Turn the schedule into a reliable starting point for the day',
        body: 'Appointments should trigger the right reminders, intake steps, staff visibility, and follow-up work automatically so the team is not running the clinic from side checklists.',
        bullets: ['Automate reminders and confirmations before the visit', 'Track pre-visit readiness before the patient arrives', 'Reduce the handoffs between booking, check-in, and care delivery'],
      },
    ],
    capabilities: [
      {
        title: 'Shared scheduling that reflects real clinic constraints',
        body: 'Coordinate providers, visit types, rooms, and patient demand from one scheduling flow that keeps operational reality in view.',
      },
      {
        title: 'Better appointment readiness',
        body: 'Bookings can trigger confirmations, intake, and patient-facing tasks so the day starts with fewer surprises and less scrambling.',
      },
      {
        title: 'Telehealth without a disconnected workflow',
        body: 'Virtual visits stay attached to the same appointment and patient context instead of being managed in a separate system.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Patient portal appointments view with upcoming visits and actions',
        title: 'Appointments patients can manage without calling the clinic',
        body: 'Patients can review upcoming visits, confirm details, and act on the appointment without creating extra back-and-forth for staff.',
      },
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Telemedicine visit experience connected to the platform',
        title: 'Telehealth that stays connected to the rest of the day',
        body: 'Virtual visits stay tied to the same scheduling and patient workflow, which cuts down coordination gaps for both staff and patients.',
      },
    ],
    related: ['patient-portal', 'solo-practices', 'growing-teams', 'pricing'],
  },
  'billing-payments': {
    path: '/billing-payments',
    navGroup: 'features',
    eyebrow: 'Payments',
    title: 'Billing & payments',
    subtitle: 'Handle invoices, balances, deposits, memberships, and patient payments without sending staff into a separate billing world.',
    highlights: ['Collect sooner with less follow-up', 'See balances clearly', 'Support packages and memberships'],
    sections: [
      {
        title: 'Make payment collection part of the normal workflow',
        body: 'Billing should not feel like something the clinic has to hand off to a different system and different process. Elen1 helps staff manage charges, deposits, invoices, and payment links inside the same workflow they already use for the visit.',
        bullets: ['Send payment links and track invoices without leaving the platform', 'Keep deposits and balances visible while the visit is still in motion', 'Support packages and recurring memberships without extra admin work'],
      },
      {
        title: 'Give the team a cleaner picture of what is owed and what is paid',
        body: 'Front desk staff, managers, and billing teams should not need to assemble the revenue story from multiple places. Elen1 keeps payment status, balances, and follow-up needs visible in the same system as the patient and visit.',
        bullets: ['See payment status across patients and appointments without manual reconciliation', 'Keep billing context visible for front desk teams and managers', 'Reduce the gap between care delivery and revenue follow-through'],
      },
    ],
    capabilities: [
      {
        title: 'Collection flows that stay close to the visit',
        body: 'Charges, invoices, and balances stay attached to the patient and appointment workflow so staff are not jumping across disconnected systems.',
      },
      {
        title: 'Better visibility into what is still outstanding',
        body: 'Front desk teams and managers can see what is due, what is paid, and what still needs follow-up without rebuilding the revenue picture elsewhere.',
      },
      {
        title: 'Support recurring revenue models cleanly',
        body: 'Packages, deposits, and memberships are easier to manage when they live inside the same operating system as the rest of the clinic.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/billing-standalone.png',
        alt: 'Billing workspace with invoices, totals, line items, and patient balances',
        title: 'Billing that stays connected to the patient workflow',
        body: 'Invoice creation, balances, and payment status stay connected to the patient record so the clinic can collect without adding extra handoffs.',
      },
      {
        src: '/landing-captures/reports-standalone.png',
        alt: 'Operational reporting workspace for building finance and performance reports',
        title: 'Revenue reporting people can trust faster',
        body: 'Finance and operational reporting come from the same system that captured the underlying activity, which cuts down reconciliation work.',
      },
    ],
    related: ['reports-insights', 'aesthetics-wellness', 'pricing', 'book-demo'],
  },
  'multi-site-operations': {
    path: '/multi-site-operations',
    navGroup: 'features',
    eyebrow: 'Operations',
    title: 'Multi-site operations',
    subtitle: 'Run multiple clinics with shared standards, clear permissions, and one view of what is happening across locations.',
    highlights: ['Consistent standards across sites', 'Permissions that match each location', 'Clearer oversight as the group grows'],
    sections: [
      {
        title: 'Create consistency without micromanaging every location',
        body: 'Growing clinic groups need shared standards, templates, and permissions without forcing every site to work in an unnatural way. Elen1 helps leadership create structure while still leaving room for local reality.',
        bullets: ['Set location-aware access and controls by site and role', 'Roll out shared templates and processes without rebuilding them at every clinic', 'Give leaders and operators a clearer cross-site view of what is working and what is drifting'],
      },
      {
        title: 'Keep operations, staffing, and reporting on the same model',
        body: 'Rooms, staffing, permissions, and reporting are easier to manage when they live in the same system as scheduling and patient flow. That makes expansion easier to govern and day-to-day operations easier to compare.',
        bullets: ['Manage rooms and shared resources with more consistency', 'See staffing and workload across sites instead of one clinic at a time', 'Keep operational controls aligned as the organization expands'],
      },
    ],
    capabilities: [
      {
        title: 'Cross-site control without extra admin layers',
        body: 'Keep locations, resources, and permissions organized from one platform instead of bolting together separate administrative tools.',
      },
      {
        title: 'Shared standards with room for local nuance',
        body: 'Roll out common workflows and reporting expectations while still allowing sites to adapt execution to the reality on the ground.',
      },
      {
        title: 'See expansion risk earlier',
        body: 'Leadership can spot staffing gaps, capacity issues, and operational drift before they become systemic across the wider group.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Operations dashboard with revenue, active patients, and upcoming appointments',
        title: 'A shared view across locations',
        body: 'Leaders can review workload, patient demand, and overall performance from one dashboard instead of pulling site snapshots one by one.',
      },
      {
        src: '/landing-captures/staff-operations-standalone.png',
        alt: 'Custom report builder for filtering and comparing operational performance',
        title: 'Reporting that makes cross-site comparison easier',
        body: 'Structured reporting helps teams compare clinics, roles, and service lines using the same operational framework.',
      },
    ],
    related: ['multi-location-groups', 'reports-insights', 'security', 'pricing'],
  },
  'reports-insights': {
    path: '/reports-insights',
    navGroup: 'features',
    eyebrow: 'Operations',
    title: 'Reports & insights',
    subtitle: 'See revenue, utilization, patient follow-through, and operational trends in one reporting workspace the team can actually use.',
    highlights: ['Executive visibility without spreadsheet lag', 'Operational KPIs people act on', 'Clinical and revenue signals together'],
    sections: [
      {
        title: 'See the numbers that actually change how the clinic runs',
        body: 'Leadership teams do not just need more charts. They need to connect patient experience, provider utilization, and revenue performance in a way that makes the next decision obvious.',
        bullets: ['Track retention, throughput, collections, and utilization in one reporting layer', 'Compare sites, providers, and service lines without rebuilding reports each time', 'Give operators and owners the same view of performance'],
      },
      {
        title: 'Move from reporting into action faster',
        body: 'Reporting is most useful when it helps the team answer the next operational question quickly. Elen1 keeps dashboards close to the workflows that created the numbers in the first place.',
        bullets: ['Drill from a KPI into the queue or workflow behind it', 'Track trends by clinic, provider, and service line over time', 'Spot bottlenecks before they become staffing, service, or revenue problems'],
      },
    ],
    capabilities: [
      {
        title: 'Executive and operating views from the same source',
        body: 'Leadership, operations, and finance can work from one reporting layer instead of keeping separate logic and separate spreadsheets for each team.',
      },
      {
        title: 'Custom analysis without starting a BI side project',
        body: 'Build filters, groupings, and repeatable reports inside the product instead of depending on ad hoc exports every time someone has a new question.',
      },
      {
        title: 'Reporting that stays close to action',
        body: 'Teams can move from a KPI to the underlying workflow faster because the reporting surface stays tied to the system running the clinic.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/reports-standalone.png',
        alt: 'Reports dashboard and custom report builder with filters and scheduled reports',
        title: 'Custom reports built around real clinic questions',
        body: 'Build filtered, scheduled, and exportable reports without sending the data into a separate analytics stack first.',
      },
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Operations overview dashboard showing revenue, appointments, active patients, and trends',
        title: 'Daily KPI visibility for operators and leadership',
        body: 'Keep the most important operating metrics visible in a daily dashboard so trends can be addressed before they become larger problems.',
      },
    ],
    related: ['billing-payments', 'clinic-owners', 'practice-managers', 'case-studies'],
  },
  'marketing-growth': {
    path: '/marketing-growth',
    navGroup: 'features',
    eyebrow: 'Operations',
    title: 'Marketing & growth',
    subtitle: 'Handle recalls, reviews, referrals, and patient reactivation in the same system that already knows your patients, schedule, and follow-up needs.',
    highlights: ['Recall and reactivation that fit the workflow', 'Referrals and reviews with real timing', 'Growth tied to actual clinic capacity'],
    sections: [
      {
        title: 'Start with the patients most likely to come back',
        body: 'A lot of clinic growth comes from patients who already know the practice. Elen1 helps teams stay on top of recalls, review requests, referrals, and reactivation without turning marketing into another disconnected system.',
        bullets: ['Run recall workflows for overdue follow-up and rebooking', 'Time review and referral requests around real patient milestones', 'See outreach in the context of appointments and outcomes'],
      },
      {
        title: 'Keep growth tied to what the clinic can actually deliver',
        body: 'Marketing is only useful if the clinic can absorb the demand cleanly. Elen1 keeps outreach closer to scheduling and capacity so teams can grow without creating operational whiplash.',
        bullets: ['Plan campaigns around real capacity instead of guesswork', 'Pace demand more cleanly across teams and locations', 'See what actually turns into visits, return bookings, and revenue'],
      },
    ],
    capabilities: [
      {
        title: 'Grow from the demand you already have',
        body: 'Use recalls, follow-up outreach, and patient communication to reactivate demand the clinic is already well positioned to serve.',
      },
      {
        title: 'Keep demand generation grounded in operations',
        body: 'Growth efforts are more useful when they reflect real schedule capacity and service availability instead of running in isolation.',
      },
      {
        title: 'Support retention without another stack of tools',
        body: 'Patient communication and follow-through can stay inside the broader operating model instead of becoming a separate marketing toolchain.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/marketing-standalone.png',
        alt: 'Messaging workspace with patient-linked threads and folders',
        title: 'Patient communication that still feels connected to care',
        body: 'Outreach works better when the communication stays tied to the patient and the care workflow instead of living in a disconnected campaign tool.',
      },
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Patient appointment view with follow-up actions and schedule visibility',
        title: 'Retention starts with easier follow-up and rebooking',
        body: 'When appointment visibility and self-service actions are clear, it becomes easier for patients to return and stay engaged with care over time.',
      },
    ],
    related: ['calendar-booking', 'patient-portal', 'aesthetics-wellness', 'pricing'],
  },
  'who-we-serve': {
    path: '/who-we-serve',
    navGroup: 'who-we-serve',
    eyebrow: 'Who we serve',
    title: 'Built for clinics that need operational clarity.',
    subtitle: 'Elen1 supports owners, operators, and practitioners across solo, growing, and multi-site care models.',
    highlights: ['Role-specific workflows', 'Support for different growth stages', 'Flexible across care models and specialties'],
    sections: [
      {
        title: 'Match the system to how the clinic actually runs',
        body: 'A solo clinic, a scaling specialty group, and a multi-site operator do not need the same level of control. Elen1 adapts without forcing separate products.',
        bullets: ['Operator and clinician views in one platform', 'Scheduling, financial, and patient-experience alignment', 'Configuration that scales with the business'],
      },
      {
        title: 'Support both clinical and operational depth',
        body: 'Different teams care about different outcomes. The public site should make it clear how Elen1 fits each role and stage of growth.',
        bullets: ['Pages tailored by role and specialty', 'Clearer buying and onboarding paths', 'Better storytelling for practical use cases'],
      },
    ],
    capabilities: [
      {
        title: 'Match the product story to the actual buyer',
        body: 'Owners, operators, and clinicians do not evaluate software in the same way, so the site should make those paths explicit.',
      },
      {
        title: 'Support different growth stages cleanly',
        body: 'A solo clinic, a growing practice, and a multi-location group each need a different level of structure and oversight.',
      },
      {
        title: 'Show relevance by care model and specialty',
        body: 'Use-case pages should help teams recognize how Elen1 fits their workflow before they ever book a demo.',
      },
    ],
    related: ['clinic-owners', 'solo-practices', 'primary-care-urgent-care', 'pricing'],
  },
  ...SOLUTION_CATALOG_PAGES,
  'clinic-owners': {
    path: '/clinic-owners',
    navGroup: 'who-we-serve',
    eyebrow: 'Role',
    title: 'Run your clinic with clear visibility across revenue, appointments, patients, and team performance.',
    subtitle: 'Elen1 gives clinic owners one connected view of bookings, billing, collections, staff activity, patient flow, and operational bottlenecks so issues can be caught early and decisions do not depend on delayed reports or manual spreadsheets.',
    highlights: ['Owner-level reporting without delay', 'Revenue and capacity visibility', 'Stronger controls across teams'],
    sections: [
      {
        title: 'Catch performance issues before they turn into revenue problems',
        body: 'Clinic owners need to see what is changing while there is still time to respond. Elen1 brings appointments, cancellations, utilization, patient demand, collections, and staff workload into one operating view so underperformance is visible before it becomes a month-end surprise.',
        bullets: ['Track appointments, cancellations, no-shows, utilization, and collections from one connected system', 'Spot where revenue is slipping because schedules are underfilled, claims are delayed, or follow-up is inconsistent', 'Review business performance without waiting for manual updates from multiple teams'],
      },
      {
        title: 'Standardize daily operations across reception, clinical, billing, and admin teams',
        body: 'When each team works from different trackers, reports, or side conversations, owners lose visibility and accountability. Elen1 helps standardize daily workflows so the front desk, clinical team, billing team, and management all work from the same operational picture.',
        bullets: ['Create clearer accountability across reception, clinical, billing, and admin workflows', 'Use role-based access and shared processes to keep teams aligned without overexposing data', 'Manage one clinic or multiple branches with the same reporting structure, operating rules, and workflow visibility'],
      },
    ],
    capabilities: [
      {
        title: 'Replace manual reporting',
        body: 'Bring appointments, revenue, billing, collections, and operational activity into one reporting layer so owners are not waiting on spreadsheets or manual summaries to understand performance.',
      },
      {
        title: 'Improve utilization',
        body: 'See schedule capacity, cancellations, no-shows, provider workload, and patient flow together so empty slots, bottlenecks, and missed follow-up are easier to address.',
      },
      {
        title: 'Standardize operations',
        body: 'Use shared workflows, role-based controls, and clearer accountability across teams so the clinic runs more consistently as volume, staff, or locations grow.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Operations overview dashboard with revenue, appointment, and active patient reporting',
        title: 'Owner overview',
        body: 'Use this view to understand how the clinic is performing today across revenue, appointments, patient activity, and operational pressure without waiting for a manual summary from the team.',
      },
      {
        src: '/landing-captures/reports-standalone.png',
        alt: 'Custom report builder with preset reports, filters, and export controls',
        title: 'Reporting and accountability',
        body: 'Use reporting to compare providers, service lines, or branches, review trends over time, and hold teams accountable with the same numbers instead of separate spreadsheets.',
      },
      {
        src: '/landing-captures/staff-operations-standalone.png',
        alt: 'Operational workspace for monitoring staff activity, coordination, and location-level controls',
        title: 'Operational controls',
        body: 'Use this area to see how work is moving across teams, reinforce consistent processes, and keep tighter control over roles, responsibilities, and day-to-day execution.',
      },
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Scheduling workspace showing appointments, calendar utilization, and clinic workload visibility',
        title: 'Workflow visibility',
        body: 'Use scheduling and workflow visibility to monitor bookings, cancellations, open capacity, and team workload so issues can be addressed before they affect patients or revenue.',
      },
    ],
    cta: {
      eyebrow: 'For clinic owners',
      title: 'Run your clinic with clearer visibility and fewer blind spots.',
      body: 'Elen1 brings scheduling, patient records, billing, reporting, and operations into one connected system so owners can run the clinic with better visibility and less manual follow-up.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'See appointments, revenue, patient flow, billing, and staff activity from one operating view',
        'Reduce dependence on spreadsheets, side updates, and delayed reporting across teams',
        'Keep role-based control and operational consistency as the clinic or branch network grows',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help clinic owners?',
        answer: 'Elen1 gives clinic owners a connected view of appointments, revenue, billing, patients, staff activity, and operations so they can run the business with clearer visibility and fewer blind spots.',
      },
      {
        question: 'How does Elen1 help manage clinic performance?',
        answer: 'It helps owners monitor utilization, cancellations, no-shows, collections, workload, and operational bottlenecks in one system so issues can be identified and addressed earlier.',
      },
      {
        question: 'Can Elen1 support multiple clinics or branches?',
        answer: 'Yes. Elen1 helps owners compare performance across locations, apply shared operating standards, and keep reporting more consistent as the organization grows.',
      },
      {
        question: 'Does Elen1 replace spreadsheets and manual reports?',
        answer: 'It is designed to reduce the need for spreadsheet-based reporting by keeping scheduling, billing, patient activity, and operational data connected in one reporting workflow.',
      },
      {
        question: 'Can I control what each team member can access?',
        answer: 'Yes. Elen1 includes role-based access controls so owners can give each team access to the workflows and information they need without exposing everything to everyone.',
      },
    ],
    relatedDescriptions: {
      'reports-insights': 'Review clinic performance, utilization, revenue, and operational trends without waiting for manual reporting.',
      'billing-payments': 'Understand how billing, balances, and collections connect to the underlying visit workflow.',
      'multi-site-operations': 'See how Elen1 helps standardize reporting, permissions, and oversight across multiple clinics or branches.',
      'practice-managers': 'Explore the day-to-day operational view used by the managers responsible for keeping the clinic running smoothly.',
    },
    related: ['reports-insights', 'billing-payments', 'multi-site-operations', 'practice-managers'],
  },
  'practice-managers': {
    path: '/practice-managers',
    navGroup: 'who-we-serve',
    eyebrow: 'For practice managers',
    title: 'Keep every part of the clinic moving from one place.',
    subtitle: 'Elen1 helps practice managers coordinate schedules, patient queues, rooms, staff tasks, handoffs, and follow-ups without relying on scattered messages, spreadsheets, or disconnected systems.',
    highlights: ['Manage the day’s schedule and patient flow', 'Coordinate handoffs across teams', 'Spot delays and missed follow-ups faster'],
    sections: [
      {
        title: 'Run the clinic day without chasing updates',
        body: 'Practice managers should not have to piece together the schedule, queue, room usage, patient status, and team updates from separate systems. Elen1 keeps the moving parts in one place so you can see what is on time, what is delayed, and where the day needs attention.',
        bullets: ['Monitor appointments, queues, rooms, and patient status from one operational view', 'See where delays, no-shows, bottlenecks, or overbooked resources are affecting flow', 'Reduce the need to chase manual updates across reception, clinical, billing, and admin teams'],
      },
      {
        title: 'Keep handoffs, tasks, and follow-ups from falling through the cracks',
        body: 'Daily clinic operations depend on strong coordination between reception, clinical, billing, and admin teams. Elen1 helps practice managers keep handoffs visible, reduce duplicated work, and make sure follow-ups, billing coordination, and unresolved tasks do not disappear between teams.',
        bullets: ['Coordinate front desk, clinical, billing, and admin work with clearer task ownership', 'Keep internal notes, handoffs, and status updates tied to the patient or workflow instead of scattered across messages', 'Reduce missed follow-ups, duplicated work, and unresolved issues at the end of the day'],
      },
    ],
    capabilities: [
      {
        title: 'Control daily queues and schedules',
        body: 'Keep the day’s appointments, patient queues, room usage, and schedule changes visible in one place so the clinic can respond faster when things shift.',
      },
      {
        title: 'Coordinate teams without scattered messages',
        body: 'Keep reception, clinical, billing, and admin teams aligned with shared workflow visibility instead of relying on side messages, separate trackers, or verbal updates.',
      },
      {
        title: 'Reduce missed handoffs and follow-ups',
        body: 'Track unresolved tasks, follow-up work, and cross-team handoffs more clearly so patient issues and operational tasks do not get lost between steps.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Scheduling workspace showing appointments, queues, and clinic-day control for operations staff',
        title: 'Queue and schedule control',
        body: 'Use this view to manage the day’s bookings, patient queue, room pressure, and schedule changes so the clinic can keep moving when delays or last-minute changes happen.',
      },
      {
        src: '/landing-captures/messages-standalone.png',
        alt: 'Internal coordination workspace with team notes, patient-linked conversations, and handoff visibility',
        title: 'Team handoffs and internal notes',
        body: 'Use this area to coordinate internal updates, clarify who owns the next step, and keep handoffs tied to the patient or workflow instead of getting lost in separate messages.',
      },
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Daily operations dashboard showing appointments, workload, patient activity, and clinic performance at a glance',
        title: 'Daily operations dashboard',
        body: 'Use the dashboard to get a quick read on how the clinic day is going across appointments, workload, patient flow, and operational pressure without waiting for staff check-ins.',
      },
      {
        src: '/landing-captures/staff-operations-standalone.png',
        alt: 'Operational controls workspace for staff coordination, team responsibilities, and clinic workflow monitoring',
        title: 'Internal coordination',
        body: 'Use this workspace to keep teams aligned around responsibilities, track where work is building up, and reinforce consistent processes across front desk, clinical, billing, and admin staff.',
      },
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Patient workflow view used to monitor pending tasks, status updates, and follow-up coordination',
        title: 'Pending tasks and follow-ups',
        body: 'Use this view to see which patients still need action, which follow-ups are unresolved, and where work may be stalled between visit completion, billing, or patient communication.',
      },
    ],
    cta: {
      eyebrow: 'For practice managers',
      title: 'Keep the clinic day organized from first booking to final follow-up.',
      body: 'Elen1 connects scheduling, patient flow, clinical activity, billing coordination, and admin tasks so practice managers can run daily operations with fewer gaps.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Keep schedules, queues, rooms, and patient status visible in one operational system',
        'Coordinate reception, clinical, billing, and admin teams without relying on scattered updates',
        'Reduce missed handoffs, unresolved tasks, and manual follow-up across the clinic day',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help practice managers?',
        answer: 'Elen1 helps practice managers run the clinic day by connecting schedules, queues, rooms, team tasks, handoffs, billing coordination, and follow-ups in one system.',
      },
      {
        question: 'How does Elen1 help during a busy clinic day?',
        answer: 'It gives managers one place to monitor appointments, patient flow, room usage, delays, staff coordination, and outstanding work so problems can be addressed earlier.',
      },
      {
        question: 'Can Elen1 help reduce missed handoffs?',
        answer: 'Yes. It keeps tasks, status changes, and team coordination closer to the patient and workflow so important next steps are less likely to be missed between teams.',
      },
      {
        question: 'Does Elen1 replace manual tracking and scattered messages?',
        answer: 'It is designed to reduce reliance on spreadsheets, side messages, and disconnected tracking by keeping daily clinic operations in one shared workflow system.',
      },
      {
        question: 'Can different teams use Elen1 together?',
        answer: 'Yes. Reception, clinical, billing, and admin teams can work from the same system while staying aligned around schedules, patient status, tasks, and follow-up responsibilities.',
      },
    ],
    relatedDescriptions: {
      'calendar-booking': 'See how scheduling, rooms, reminders, and appointment control support the clinic day in real time.',
      'multi-site-operations': 'Explore the operational controls used to manage teams, workflows, and visibility across locations.',
      'help-center': 'Review practical guidance for setup, daily workflows, and support questions from clinic teams.',
      'book-demo': 'Book a walkthrough focused on day-to-day clinic operations, coordination, and workflow control.',
    },
    related: ['calendar-booking', 'multi-site-operations', 'help-center', 'book-demo'],
  },
  'practitioners': {
    path: '/practitioners',
    navGroup: 'who-we-serve',
    eyebrow: 'For practitioners',
    title: 'See the patient clearly, document with less friction, and keep care moving.',
    subtitle: 'Elen1 helps practitioners start visits with the right patient context, document without unnecessary backtracking, and keep follow-up, communication, and next steps connected to the record.',
    highlights: ['Start with useful patient context', 'Document and act in one workflow', 'Keep follow-up from getting disconnected'],
    sections: [
      {
        title: 'Start the visit with the information that actually matters',
        body: 'Practitioners should not have to reconstruct the patient story from multiple tabs, notes, and messages before they can begin. Elen1 keeps history, medications, recent activity, open tasks, and follow-up context closer to the chart so the visit can start with less searching.',
        bullets: ['Review the patient history, recent activity, and open follow-up items from one place', 'Keep labs, medications, notes, and communication closer to the encounter workflow', 'Spend less time hunting for context before making a clinical decision'],
      },
      {
        title: 'Move from assessment to plan without re-entering the same work',
        body: 'After the visit, the work should not split across disconnected documentation, messages, and follow-up tools. Elen1 helps practitioners document, communicate, and hand off next steps from the same workflow so care can move forward with less duplication.',
        bullets: ['Document, assign follow-up, and keep communication tied to the patient record', 'Reduce duplicated entry between in-person care, virtual care, and follow-up tasks', 'Make it easier to carry the plan forward after the note is finished'],
      },
    ],
    capabilities: [
      {
        title: 'Prepare faster for each encounter',
        body: 'Get to the relevant history, recent activity, and open issues more quickly so time is spent on care instead of finding context.',
      },
      {
        title: 'Document and coordinate in one place',
        body: 'Keep documentation, messages, and next steps closer together so clinicians spend less time reworking the visit after it ends.',
      },
      {
        title: 'Keep continuity between visits',
        body: 'Carry the patient context, communication, and follow-up plan forward instead of letting care split into separate workstreams after the encounter.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Patient detail view with AI-generated longitudinal clinical snapshot, highlights, and care gaps',
        title: 'Patient context before the note starts',
        body: 'Use this view to review the patient story, recent activity, and outstanding issues before or during the visit so clinical decisions start with the right context.',
      },
      {
        src: '/landing-captures/messages-standalone.png',
        alt: 'Telemedicine workspace with live diagnostics, red flags, and AI summary during a visit',
        title: 'Communication and follow-up that stay tied to care',
        body: 'Use this workflow to manage between-visit communication, virtual care, and follow-up questions without losing the clinical thread or switching into separate tools.',
      },
    ],
    cta: {
      eyebrow: 'For practitioners',
      title: 'Give clinicians clearer context and less administrative drag.',
      body: 'Elen1 keeps patient history, documentation, communication, and follow-up connected so practitioners can focus more on the visit and less on reconstructing the workflow around it.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Start visits with the relevant patient context already visible',
        'Keep documentation, communication, and next steps closer together',
        'Reduce fragmented follow-up across in-person and virtual care',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help practitioners?',
        answer: 'Elen1 helps practitioners review patient context, document care, and keep communication and follow-up connected in one workflow.',
      },
      {
        question: 'How does Elen1 help during the visit?',
        answer: 'It gives clinicians faster access to the patient history, recent activity, medications, notes, and open follow-up items so they spend less time searching before making decisions.',
      },
      {
        question: 'Can Elen1 reduce duplicated documentation work?',
        answer: 'It is designed to keep documentation, patient communication, and next steps closer together so practitioners do not have to re-enter the same information across separate tools.',
      },
      {
        question: 'Does it support virtual care and follow-up too?',
        answer: 'Yes. Virtual care, communication, and follow-up can stay tied to the same patient context instead of becoming separate workstreams.',
      },
      {
        question: 'Can the care team work from the same patient context?',
        answer: 'Yes. Elen1 helps clinicians and support staff stay aligned around the same record, follow-up tasks, and communication history.',
      },
    ],
    relatedDescriptions: {
      'clinical-ai': 'See how summaries, prompts, and decision support fit directly into the clinical workflow.',
      'patient-portal': 'Explore how patients can complete forms, view instructions, and stay connected between visits.',
      'mental-health-therapy': 'See how Elen1 supports recurring care, longitudinal notes, and between-session communication.',
      'primary-care-urgent-care': 'Review how the platform supports faster intake, triage, and continuity in higher-volume care settings.',
    },
    related: ['clinical-ai', 'patient-portal', 'mental-health-therapy', 'primary-care-urgent-care'],
  },
  'solo-practices': {
    path: '/solo-practices',
    navGroup: 'who-we-serve',
    eyebrow: 'For solo practices',
    title: 'Run the whole practice from one system without creating extra admin.',
    subtitle: 'Elen1 helps solo practices manage booking, records, billing, and patient communication in one connected workflow so the day is not held together by spreadsheets, manual updates, or disconnected tools.',
    highlights: ['Keep the day simple', 'Collect and follow up in one workflow', 'Add structure before growth creates chaos'],
    sections: [
      {
        title: 'Keep the clinic day simple instead of juggling software',
        body: 'A solo practice does not have extra time for system maintenance, duplicate entry, or chasing information across separate apps. Elen1 helps keep bookings, charting, billing, and patient communication in one place so the practice can run with less admin overhead.',
        bullets: ['Use one system for scheduling, charting, billing, and patient follow-up', 'Reduce duplicate updates between appointments, documentation, and payment workflows', 'Keep day-to-day operations manageable without relying on workarounds'],
      },
      {
        title: 'Put better structure in place before the practice grows',
        body: 'Even a lean clinic needs clear processes. Elen1 gives solo practices a stronger operating foundation so adding another provider, assistant, or receptionist does not force a full reset in how the clinic works.',
        bullets: ['Build repeatable workflows before volume and staffing increase', 'Add structure for billing, reporting, and patient follow-up while the practice is still small', 'Grow into a larger team without rebuilding the software stack'],
      },
    ],
    capabilities: [
      {
        title: 'Replace disconnected tools',
        body: 'Keep scheduling, records, payments, and patient communication together so the practice can run with fewer systems and fewer manual handoffs.',
      },
      {
        title: 'Handle billing and follow-up in the same workflow',
        body: 'Keep invoices, balances, and patient follow-up closer to the visit so collection work is easier to manage on time.',
      },
      {
        title: 'Grow without rebuilding the stack',
        body: 'Add staff, volume, and structure on top of one operating system instead of replacing tools once the clinic gets busier.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Operations dashboard with appointments, revenue, active patients, and schedule overview',
        title: 'A single daily view for a lean team',
        body: 'Use the dashboard to see bookings, patient activity, and revenue in one place so the practice owner or small team can start the day with a clear picture.',
      },
      {
        src: '/landing-captures/billing-standalone.png',
        alt: 'Patient billing workspace with invoices, billing codes, and outstanding totals',
        title: 'Billing that stays close to the visit',
        body: 'Use the billing workflow to handle charges, balances, and patient payments without moving into a separate system or manual tracking process.',
      },
    ],
    cta: {
      eyebrow: 'For solo practices',
      title: 'Keep the practice simple, connected, and easier to run.',
      body: 'Elen1 brings booking, records, billing, and patient communication into one workflow so solo practices can stay organized without adding more admin overhead.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Run scheduling, charting, billing, and follow-up from one system',
        'Reduce spreadsheet work and duplicate updates across the day',
        'Build better structure before the practice grows into a larger team',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help solo practices?',
        answer: 'Elen1 helps solo practices run booking, records, billing, and patient communication in one connected system instead of relying on separate tools and manual workarounds.',
      },
      {
        question: 'How does Elen1 help a lean clinic team?',
        answer: 'It reduces duplicate work by keeping day-to-day scheduling, documentation, payments, and follow-up closer together in one workflow.',
      },
      {
        question: 'Can Elen1 help with billing and payments too?',
        answer: 'Yes. It helps solo practices manage invoices, balances, and patient payments without moving billing into a separate disconnected tool.',
      },
      {
        question: 'Will it still work if the practice grows?',
        answer: 'Yes. The platform is designed to give small clinics a stronger operating base so they can add staff and volume without needing to replace the system later.',
      },
      {
        question: 'Does Elen1 reduce reliance on spreadsheets?',
        answer: 'That is the goal. It keeps more of the clinic workflow inside one system so fewer updates need to be tracked manually outside the platform.',
      },
    ],
    relatedDescriptions: {
      'calendar-booking': 'See how appointment scheduling, reminders, and calendar control fit a lean clinic workflow.',
      'billing-payments': 'Explore how invoices, balances, and payments stay connected to the patient visit.',
      'pricing': 'Review the pricing model for clinics that want one system instead of a fragmented stack.',
      'book-demo': 'Book a walkthrough focused on how a solo practice can run day-to-day work more simply.',
    },
    related: ['calendar-booking', 'billing-payments', 'pricing', 'book-demo'],
  },
  'growing-teams': {
    path: '/growing-teams',
    navGroup: 'who-we-serve',
    eyebrow: 'For growing teams',
    title: 'Add providers and staff without letting the clinic get harder to run.',
    subtitle: 'Elen1 helps growing clinics standardize scheduling, handoffs, team responsibilities, and reporting as more people, more appointments, and more daily complexity move through the same operation.',
    highlights: ['Shared workflows across a bigger team', 'Clearer role ownership', 'Better visibility as volume grows'],
    sections: [
      {
        title: 'Expand capacity without letting daily workflows drift',
        body: 'As clinics add providers, support staff, and new services, small process gaps quickly become operational problems. Elen1 helps growing teams keep scheduling, handoffs, and responsibilities more consistent so the clinic can scale without becoming harder to manage.',
        bullets: ['Use shared calendars, workflows, and role-based processes across a larger team', 'Keep scheduling, patient flow, and follow-up expectations more consistent as responsibilities split', 'Reduce friction between front desk, clinical, billing, and management teams'],
      },
      {
        title: 'Give management more control before complexity turns into drag',
        body: 'Growing teams need more than extra headcount. They need clearer operating rules, better reporting, and workflows that new staff can step into quickly. Elen1 helps make the clinic easier to manage as complexity rises.',
        bullets: ['Onboard new staff into more consistent scheduling and operational processes', 'Keep operational and clinical workflows closer together instead of adding more side systems', 'See where growth is creating delays, uneven workloads, or inconsistent execution'],
      },
    ],
    capabilities: [
      {
        title: 'Shared workflows for a larger team',
        body: 'Give new and existing staff clearer operational standards so the clinic does not depend on informal workarounds as it grows.',
      },
      {
        title: 'Clearer coordination across roles',
        body: 'Keep communication, handoffs, and follow-up closer to the workflow so responsibilities remain clear as teams become more specialized.',
      },
      {
        title: 'Reporting that grows with the clinic',
        body: 'Give management a better view of workload, performance, and operational drift as headcount and patient volume increase.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Operations overview dashboard for appointments, revenue, and patient volume',
        title: 'A shared daily view as the team expands',
        body: 'Use the dashboard to review appointments, workload, and patient demand across a larger team so pressure points are visible before they become daily friction.',
      },
      {
        src: '/landing-captures/reports-standalone.png',
        alt: 'Custom report builder with filters, grouping, and scheduling options',
        title: 'Reporting with more structure as the clinic grows',
        body: 'Use reporting to understand performance by provider, workflow, or service line without falling back on manual data collection as complexity increases.',
      },
    ],
    cta: {
      eyebrow: 'For growing teams',
      title: 'Grow the clinic without adding operational confusion.',
      body: 'Elen1 helps growing teams keep scheduling, responsibilities, reporting, and follow-up more consistent as more staff and patient volume move through the same system.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Standardize daily workflows across front desk, clinical, billing, and management teams',
        'Onboard new staff into clearer processes instead of informal workarounds',
        'See where growth is creating delays, uneven workload, or workflow drift',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help growing teams?',
        answer: 'Elen1 helps growing clinics keep scheduling, handoffs, reporting, and team responsibilities more consistent as headcount and patient volume increase.',
      },
      {
        question: 'How does Elen1 help as the clinic gets busier?',
        answer: 'It gives teams shared workflows and better visibility so the clinic can manage more providers, more staff, and more operational complexity without losing control.',
      },
      {
        question: 'Can it help onboard new staff into clearer processes?',
        answer: 'Yes. Elen1 supports more consistent workflows so new hires can step into established operating patterns instead of learning through scattered tribal knowledge.',
      },
      {
        question: 'Does Elen1 help management see operational drift?',
        answer: 'Yes. It helps leaders review workload, performance, and day-to-day execution so process gaps are easier to spot as the team grows.',
      },
      {
        question: 'Can different departments use the same system?',
        answer: 'Yes. Front desk, clinical, billing, and management teams can work from the same platform while maintaining clearer role ownership.',
      },
    ],
    relatedDescriptions: {
      'practice-managers': 'See the operational view used to coordinate the clinic day, staff handoffs, and patient flow.',
      'multi-site-operations': 'Explore the controls used when growing teams expand into more locations and more structured governance.',
      'reports-insights': 'Review how management can track performance with more structure as the business grows.',
      'pricing': 'Review pricing for clinics that need a system that can scale with team size and complexity.',
    },
    related: ['practice-managers', 'multi-site-operations', 'reports-insights', 'pricing'],
  },
  'multi-location-groups': {
    path: '/multi-location-groups',
    navGroup: 'who-we-serve',
    eyebrow: 'For multi-location groups',
    title: 'Run multiple clinics with clearer reporting, tighter controls, and fewer local blind spots.',
    subtitle: 'Elen1 helps multi-location groups compare site performance, standardize workflows, manage permissions, and keep daily operations visible across the network without losing local flexibility.',
    highlights: ['Cross-site visibility', 'Shared controls and standards', 'Local teams with central oversight'],
    sections: [
      {
        title: 'Operate every clinic from the same reporting and workflow model',
        body: 'Multi-location groups need consistency without forcing every site to work in exactly the same way. Elen1 helps central teams standardize permissions, reporting, and core workflows while still giving local clinics room to operate effectively day to day.',
        bullets: ['Compare locations using shared reporting definitions and workflow rules', 'Roll out common templates, permissions, and standards across the network', 'Keep local clinics moving while maintaining central oversight'],
      },
      {
        title: 'See where performance is drifting before it becomes a network problem',
        body: 'When one site starts slipping, leadership needs to know early. Elen1 helps compare throughput, capacity, revenue, and execution across clinics so local issues can be addressed before they become broader operational problems.',
        bullets: ['Review performance by clinic, region, or service line from one reporting layer', 'Spot process drift, utilization issues, or uneven execution earlier', 'Support stronger staffing, remediation, and expansion decisions across the group'],
      },
    ],
    capabilities: [
      {
        title: 'Central reporting across the group',
        body: 'Review performance across clinics from one reporting layer instead of relying on separate exports and local spreadsheets from each site.',
      },
      {
        title: 'Governance that scales cleanly',
        body: 'Carry permissions, workflow standards, and reporting expectations across more sites without growing manual administrative work at the same pace.',
      },
      {
        title: 'Local execution with shared oversight',
        body: 'Let local teams keep working at speed while leadership maintains a clearer view of operations, compliance, and site-level performance.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/reports-standalone.png',
        alt: 'Custom reporting workspace for filtering, grouping, and scheduling site-level reports',
        title: 'Cross-site reporting with more control',
        body: 'Use reporting to compare clinics, providers, and service lines with the same definitions so performance reviews are more consistent across the group.',
      },
      {
        src: '/landing-captures/dashboard-standalone.png',
        alt: 'Operations overview dashboard with revenue, active patients, and appointment metrics',
        title: 'One operating lens across multiple clinics',
        body: 'Use the dashboard to monitor capacity, revenue, patient demand, and operational pressure across the network without waiting for separate site updates.',
      },
    ],
    cta: {
      eyebrow: 'For multi-location groups',
      title: 'Manage multiple clinics with clearer oversight and better consistency.',
      body: 'Elen1 connects reporting, permissions, workflows, and operational visibility across locations so leadership can run the network with fewer blind spots and less manual reconciliation.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Compare clinic performance using shared reporting and workflow definitions',
        'Standardize permissions and processes across more sites with less manual admin',
        'See local issues earlier before they become broader network problems',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help multi-location groups?',
        answer: 'Elen1 helps multi-location groups manage reporting, permissions, workflows, and site performance from one connected operating system.',
      },
      {
        question: 'How does Elen1 help leadership compare sites?',
        answer: 'It gives leadership one reporting layer for reviewing clinics, providers, and service lines using shared definitions instead of separate local reports.',
      },
      {
        question: 'Can local clinics still keep some flexibility?',
        answer: 'Yes. Elen1 is designed to support central standards and oversight without removing the day-to-day flexibility local teams need to operate well.',
      },
      {
        question: 'Does Elen1 help with permissions and governance?',
        answer: 'Yes. It supports role-based controls and more consistent workflow standards across the organization as more sites are added.',
      },
      {
        question: 'Can it reduce manual reconciliation across locations?',
        answer: 'That is one of the main benefits. It helps bring reporting and operations into one system so leadership is less dependent on separate site-level exports and spreadsheets.',
      },
    ],
    relatedDescriptions: {
      'multi-site-operations': 'Explore the operational controls used to manage locations, roles, rooms, staff, and governance across the network.',
      'reports-insights': 'See how performance, revenue, and capacity reporting can be reviewed across multiple clinics.',
      'security': 'Review the controls that help larger organizations manage access, auditability, and operational governance.',
      'case-studies': 'See practical examples of how more structured operations can support multi-site growth and consistency.',
    },
    related: ['multi-site-operations', 'reports-insights', 'security', 'case-studies'],
  },
  'primary-care-urgent-care': {
    path: '/primary-care-urgent-care',
    navGroup: 'who-we-serve',
    eyebrow: 'For primary care and urgent care',
    title: 'Handle high-volume intake, triage, visits, and follow-up without losing continuity.',
    subtitle: 'Elen1 helps primary care and urgent care teams manage intake, triage, documentation, lab follow-up, telemedicine, and next steps in one connected workflow so speed does not come at the cost of control.',
    highlights: ['Faster intake under pressure', 'Clearer triage and visit flow', 'Better follow-up after the encounter'],
    sections: [
      {
        title: 'Keep intake and triage moving when volume changes quickly',
        body: 'Primary care and urgent care teams need to move fast without losing the thread of the visit. Elen1 helps keep intake, triage, and patient flow visible so teams can respond to changing demand with more structure and less guesswork.',
        bullets: ['Keep queues, arrivals, and triage status visible as demand shifts during the day', 'Support faster handoffs between intake, assessment, and next-step planning', 'Reduce delays caused by switching between separate queue, chart, and communication tools'],
      },
      {
        title: 'Make follow-up easier after the visit ends',
        body: 'The job is not done when the encounter closes. Lab review, patient messaging, medication follow-up, and virtual care all affect continuity. Elen1 helps keep those next steps closer to the visit so unresolved work is easier to see and complete.',
        bullets: ['Keep follow-up signals visible across labs, messaging, and patient communication', 'Reduce the risk of unresolved items being lost after the visit', 'Support better continuity across in-person and virtual care'],
      },
    ],
    capabilities: [
      {
        title: 'Triage and queue clarity',
        body: 'Keep high-volume intake more organized so the clinic can respond faster when arrivals and urgency levels change quickly.',
      },
      {
        title: 'Faster review of results and next steps',
        body: 'Keep lab review, follow-up tasks, and unresolved next steps visible so they do not linger after the encounter.',
      },
      {
        title: 'Virtual care that stays connected',
        body: 'Keep telemedicine and follow-up visits tied to the same patient context instead of treating them as a separate workflow.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Walk-in urgent care flow with waiting queue, urgency scoring, and visit actions',
        title: 'Queue and triage support for faster intake',
        body: 'Use this workflow to manage arrivals, triage status, and visit flow so high-volume intake stays organized even when demand changes quickly.',
      },
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Lab reporting workspace with abnormal flags, selected report details, and workflow status',
        title: 'Lab review and follow-up that stay tied to care',
        body: 'Use this view to keep result review, abnormal follow-up, and patient next steps visible after the visit so continuity does not depend on separate manual tracking.',
      },
    ],
    cta: {
      eyebrow: 'For primary care and urgent care',
      title: 'Keep fast-moving care more organized from intake to follow-up.',
      body: 'Elen1 connects queue management, charting, patient flow, lab follow-up, and telemedicine so primary and urgent care teams can move faster without losing continuity.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Manage intake, triage, and visit flow from one connected workflow',
        'Keep labs, follow-up, and patient communication closer to the encounter',
        'Reduce workflow breaks between in-person care, virtual care, and next steps',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help primary care and urgent care teams?',
        answer: 'Elen1 helps primary care and urgent care teams manage intake, triage, documentation, follow-up, and patient communication in one connected workflow.',
      },
      {
        question: 'How does Elen1 help during high-volume clinic days?',
        answer: 'It keeps queues, triage status, patient flow, and next steps more visible so teams can respond faster when demand changes throughout the day.',
      },
      {
        question: 'Can it help with lab follow-up after the visit?',
        answer: 'Yes. It helps keep lab review, abnormal findings, and patient follow-up tied closer to the visit so unresolved items are easier to track.',
      },
      {
        question: 'Does Elen1 support virtual care too?',
        answer: 'Yes. Telemedicine and follow-up visits can stay connected to the same patient context instead of becoming separate workflows.',
      },
      {
        question: 'Can Elen1 reduce manual tracking between teams?',
        answer: 'That is one of the goals. It helps keep intake, clinical work, and follow-up closer together so teams are less dependent on side messages and separate trackers.',
      },
    ],
    relatedDescriptions: {
      'clinical-ai': 'See how summaries, prompts, and decision support can support faster clinical review in high-volume care settings.',
      'calendar-booking': 'Explore scheduling and queue tools that help manage same-day demand, rooms, and provider availability.',
      'practitioners': 'Review the practitioner workflow for documentation, patient context, and follow-up planning.',
      'book-demo': 'Book a walkthrough focused on intake, triage, visit flow, and follow-up in primary or urgent care.',
    },
    related: ['clinical-ai', 'calendar-booking', 'practitioners', 'book-demo'],
  },
  'aesthetics-wellness': {
    path: '/aesthetics-wellness',
    navGroup: 'who-we-serve',
    eyebrow: 'For aesthetics and wellness',
    title: 'Connect consults, bookings, payments, and repeat visits in one smoother workflow.',
    subtitle: 'Elen1 helps aesthetics and wellness teams manage consults, treatment planning, packages, payments, and repeat-visit follow-up in one connected system so the patient experience feels more organized from first inquiry to return visit.',
    highlights: ['Stronger consult-to-payment flow', 'Better repeat-visit coordination', 'A smoother front-desk experience'],
    sections: [
      {
        title: 'Keep consult, booking, and payment connected',
        body: 'In aesthetics and wellness, growth depends on a smooth commercial workflow as much as the treatment itself. Elen1 helps teams move from consultation to booking, treatment planning, payment, and return visits without breaking the patient journey into disconnected steps.',
        bullets: ['Keep consult activity closer to booking and billing workflows', 'Support package sales, balances, and repeat-visit coordination with better visibility', 'Give staff a clearer view of what is booked, paid, and still needs follow-up'],
      },
      {
        title: 'Make repeat visits and front-desk coordination easier to manage',
        body: 'A more premium patient experience usually comes from cleaner operations. Elen1 helps teams manage balances, rebooking, and follow-up with less friction so staff can deliver a smoother experience before and after treatment.',
        bullets: ['Make front-desk collection and payment handling easier to manage', 'Support rebooking and patient return activity with clearer visibility', 'Keep communication and follow-up more consistent before and after treatment'],
      },
    ],
    capabilities: [
      {
        title: 'A cleaner path from consult to payment',
        body: 'Keep treatment planning, invoices, and front-desk collection closer together so conversion is easier to manage day to day.',
      },
      {
        title: 'Better support for repeat visits',
        body: 'Use scheduling and follow-up workflows that make rebooking and patient return activity easier to manage over time.',
      },
      {
        title: 'A more organized front-desk experience',
        body: 'Reduce operational friction at the desk and between visits so the clinic experience feels smoother and more intentional.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/billing-standalone.png',
        alt: 'Patient billing workspace with invoice builder, totals, and line items',
        title: 'Billing that supports a smoother commercial workflow',
        body: 'Use the billing workflow to manage line items, balances, and payments without forcing staff into disconnected tools at the point of service.',
      },
      {
        src: '/landing-captures/scheduling-standalone.png',
        alt: 'Patient portal appointments view with upcoming visits, details, and actions',
        title: 'Repeat-visit coordination that supports retention',
        body: 'Use appointment visibility and follow-up workflows to make rebooking easier for both staff and patients between treatments.',
      },
    ],
    cta: {
      eyebrow: 'For aesthetics and wellness',
      title: 'Give consults, payments, and repeat visits a more connected workflow.',
      body: 'Elen1 connects bookings, treatment planning, billing, and follow-up so aesthetics and wellness teams can run a smoother commercial and patient experience.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Keep consult, booking, and payment workflows closer together',
        'Manage packages, balances, and repeat visits with better visibility',
        'Reduce front-desk friction and support a more polished patient experience',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help aesthetics and wellness practices?',
        answer: 'Elen1 helps aesthetics and wellness teams connect consults, bookings, payments, and repeat-visit follow-up in one workflow.',
      },
      {
        question: 'How does Elen1 help the front desk and treatment team?',
        answer: 'It gives teams clearer visibility into bookings, balances, payments, and next steps so the patient journey is easier to manage from check-in through follow-up.',
      },
      {
        question: 'Can it help with repeat visits and rebooking?',
        answer: 'Yes. Elen1 helps practices coordinate future appointments and follow-up activity so rebooking is easier to manage over time.',
      },
      {
        question: 'Does it support billing and patient balances too?',
        answer: 'Yes. Billing workflows stay closer to the patient visit so teams can manage invoices, balances, and collection more cleanly.',
      },
      {
        question: 'Can Elen1 reduce disconnected front-desk workflows?',
        answer: 'That is one of the main goals. It helps keep commercial and patient-facing steps closer together instead of splitting them across separate tools.',
      },
    ],
    relatedDescriptions: {
      'billing-payments': 'Explore the billing and payment workflows used to manage balances, invoices, and collection cleanly.',
      'marketing-growth': 'See how outreach, recalls, reviews, and patient reactivation can support repeat business.',
      'pricing': 'Review pricing for practices that want a more connected system for commercial and patient workflows.',
      'case-studies': 'See practical examples of how smoother workflows can support growth, retention, and patient experience.',
    },
    related: ['billing-payments', 'marketing-growth', 'pricing', 'case-studies'],
  },
  'mental-health-therapy': {
    path: '/mental-health-therapy',
    navGroup: 'who-we-serve',
    eyebrow: 'For mental health and therapy',
    title: 'Keep recurring care, secure communication, and follow-through organized over time.',
    subtitle: 'Elen1 helps mental health and therapy teams manage recurring appointments, longitudinal notes, between-session communication, and ongoing follow-up in one connected workflow built for continuity of care.',
    highlights: ['Recurring care that stays organized', 'Secure communication between sessions', 'Better continuity over time'],
    sections: [
      {
        title: 'Support continuity across longer treatment relationships',
        body: 'Mental health and therapy care depends on steady follow-through over time. Elen1 helps teams keep recurring appointments, notes, reminders, and between-session communication organized so continuity does not depend on manual tracking.',
        bullets: ['Manage recurring appointments and follow-up with more consistency', 'Keep documentation and patient communication in a governed workflow', 'Tie between-session tasks and reminders back to the broader care plan'],
      },
      {
        title: 'Reduce administrative friction around repeat care',
        body: 'Recurring care creates a steady stream of scheduling, outreach, documentation, and patient communication work. Elen1 helps clinicians and support teams handle that work in a more connected way so ongoing treatment feels easier to maintain.',
        bullets: ['Keep patient tasks, reminders, and communication organized between sessions', 'Maintain a clearer longitudinal record for ongoing care relationships', 'Support more structured engagement over longer treatment arcs'],
      },
    ],
    capabilities: [
      {
        title: 'Recurring care that stays organized',
        body: 'Help clinicians and coordinators manage repeat appointments and ongoing follow-up with less manual overhead between sessions.',
      },
      {
        title: 'Secure communication in context',
        body: 'Keep patient messaging and team coordination tied to the record instead of scattering it across disconnected channels.',
      },
      {
        title: 'Clearer follow-through between sessions',
        body: 'Use care-plan visibility and reminders to support continuity in treatment relationships that extend over months or years.',
      },
    ],
    screenshots: [
      {
        src: '/landing-captures/messages-standalone.png',
        alt: 'Secure messaging inbox with patient-linked conversation and care-team context',
        title: 'Secure communication tied to the patient record',
        body: 'Use this workflow to manage between-session communication without losing context or relying on separate channels outside the care record.',
      },
      {
        src: '/landing-captures/patients-standalone.png',
        alt: 'Patient portal care plan view with progress, next review date, and care status',
        title: 'Longitudinal follow-through patients can actually see',
        body: 'Use this view to keep patients aligned on progress, next steps, and care-plan visibility between recurring therapy or behavioral health visits.',
      },
    ],
    cta: {
      eyebrow: 'For mental health and therapy',
      title: 'Keep recurring care and between-session follow-through better organized.',
      body: 'Elen1 connects scheduling, notes, secure communication, and follow-up so mental health and therapy teams can protect continuity with less administrative friction.',
      primaryLabel: 'Book a demo',
      secondaryLabel: 'Explore features',
      proofs: [
        'Manage recurring appointments and patient follow-up in one workflow',
        'Keep secure communication and reminders tied to the care record',
        'Support clearer continuity across treatment relationships that extend over time',
      ],
    },
    faq: [
      {
        question: 'How does Elen1 help mental health and therapy teams?',
        answer: 'Elen1 helps mental health and therapy teams manage recurring appointments, notes, secure communication, and follow-up in one connected workflow.',
      },
      {
        question: 'How does Elen1 help with recurring care?',
        answer: 'It helps teams keep repeat appointments, patient tasks, reminders, and between-session communication more organized over longer treatment relationships.',
      },
      {
        question: 'Can it support secure communication between sessions?',
        answer: 'Yes. Elen1 helps keep patient messaging tied to the care record so communication does not get lost in separate channels.',
      },
      {
        question: 'Does Elen1 help with continuity of care over time?',
        answer: 'Yes. It helps keep notes, reminders, care-plan visibility, and follow-up closer together so ongoing care is easier to manage consistently.',
      },
      {
        question: 'Can different members of the care team use the same system?',
        answer: 'Yes. Clinicians and support staff can work from the same system while staying aligned around appointments, communication, and follow-up responsibilities.',
      },
    ],
    relatedDescriptions: {
      'practitioners': 'See the clinician workflow for patient context, documentation, and next-step planning.',
      'patient-portal': 'Explore how patients can stay connected to messages, tasks, and care information between visits.',
      'clinical-ai': 'Review how summaries and clinical support can reduce re-reading and improve context during care.',
      'help-center': 'Find practical guidance for setup, workflows, and ongoing support for care teams.',
    },
    related: ['practitioners', 'patient-portal', 'clinical-ai', 'help-center'],
  },
  'why-elen1': {
    path: '/why-elen1',
    navGroup: 'why-us',
    eyebrow: 'About elen1',
    title: 'Why elen1',
    subtitle: 'Why clinics choose elen1 when they want fewer systems, clearer operations, and a rollout that feels realistic for the team.',
    highlights: ['One platform instead of a stitched-together stack', 'Operational and clinical workflows in one system', 'A rollout path teams can actually absorb'],
    sections: [
      {
        title: 'Get the clinic onto one operating system',
        body: 'Most clinics are not short on software. They are short on continuity between the systems they already use. Elen1 brings records, scheduling, patient communication, payments, and reporting into one operating layer so teams are not spending the day reconnecting the same work by hand.',
        bullets: ['Reduce the fragile integrations and side processes that build up over time', 'Cut down duplicated entry and status chasing across teams', 'Give owners, operators, and clinicians a clearer view of the same workflow'],
      },
      {
        title: 'Roll out in a way the team can realistically manage',
        body: 'The goal is not to dump a giant platform on a clinic and hope people adapt. Elen1 is meant to start with the workflows that matter most, get adoption moving, and then expand from a stable base as the team grows more confident.',
        bullets: ['Implementation grounded in day-to-day operations rather than generic software training', 'Configurable enough to fit the clinic without becoming chaotic to maintain', 'Built for real execution and follow-through, not just a polished sales demo'],
      },
    ],
    related: ['features', 'case-studies', 'security', 'comparisons'],
  },
  'case-studies': {
    path: '/case-studies',
    navGroup: 'why-us',
    eyebrow: 'Customer stories',
    title: 'Case studies',
    subtitle: 'Stories that show what actually changes when clinics move scheduling, operations, and revenue work into elen1.',
    highlights: ['Throughput gains', 'Cleaner scheduling operations', 'Revenue and retention improvements'],
    sections: [
      {
        title: 'Show the impact in terms clinics actually care about',
        body: 'Good case studies should not read like polished marketing theater. They should show what changed in day-to-day operations: fewer missed handoffs, faster documentation, smoother collections, clearer reporting, and less friction across teams.',
        bullets: ['Examples grounded in operational changes people can recognize immediately', 'Stories that help owners, managers, and clinicians see what would improve for them', 'Proof points that feel relevant across specialty and growth-stage decisions'],
      },
      {
        title: 'Make the proof feel specific to the buyer',
        body: 'Clinics want to know whether a platform will make sense in their own environment. Case studies should help people see the before-and-after in a way that feels tied to their role, team structure, and type of practice.',
        bullets: ['Role- and specialty-aware examples instead of generic success claims', 'Before-and-after framing that makes the operational shift obvious', 'Stronger buying confidence for teams that need proof before they change systems'],
      },
    ],
    related: ['clinic-owners', 'aesthetics-wellness', 'reports-insights', 'book-demo'],
  },
  'security': {
    path: '/security',
    navGroup: 'why-us',
    eyebrow: 'Trust & security',
    title: 'Security',
    subtitle: 'Protect patient data with healthcare-grade controls, audit trails, and access design that fit the way clinic teams actually work.',
    highlights: ['Access governance', 'Auditability', 'Operational trust'],
    sections: [
      {
        title: 'Protect sensitive workflows without slowing teams down',
        body: 'Security should not feel like something bolted onto the side of the product. Elen1 is designed so controlled access, traceable actions, and accountability are built into the same workflows teams already use every day.',
        bullets: ['Role-aware access patterns that make sense for real clinic responsibilities', 'Traceable actions across the workflows that matter most', 'A stronger fit for healthcare teams working in high-trust environments'],
      },
      {
        title: 'Keep governance close to operations',
        body: 'Security gets easier to manage when it lives close to permissions, workflows, and reporting instead of being spread across disconnected admin surfaces. That keeps ownership clearer and makes the system easier to govern over time.',
        bullets: ['Fewer disconnected places where admins have to manage risk', 'Clearer ownership of access, changes, and activity', 'Operational discipline and security posture staying aligned as the clinic grows'],
      },
    ],
    related: ['multi-site-operations', 'developer-api', 'resources', 'book-demo'],
  },
  'comparisons': {
    path: '/comparisons',
    navGroup: 'why-us',
    eyebrow: 'Compare approaches',
    title: 'Comparisons',
    subtitle: 'See what changes when a clinic stops stitching together separate EMR, booking, billing, and CRM tools and starts operating from one system.',
    highlights: ['All-in-one operating model', 'Less integration drag', 'Better cross-team visibility'],
    sections: [
      {
        title: 'Compare operating models, not just features',
        body: 'A feature checklist only tells part of the story. The real difference is whether the clinic still has to reconcile five disconnected systems after the sale or whether the workflow actually runs from one place.',
        bullets: ['A shared data model across scheduling, clinical, billing, and follow-up work', 'Far fewer disconnected tools for staff to reconcile throughout the day', 'Better continuity from booking to documentation to payment and retention'],
      },
      {
        title: 'Reduce hidden complexity as the clinic grows',
        body: 'Disconnected systems create reporting gaps, brittle workflows, and harder onboarding as the clinic gets busier. Elen1 is built to reduce that stack burden over time so growth does not automatically mean more operational mess.',
        bullets: ['Lower software sprawl and less process glue holding the day together', 'Cleaner onboarding for new staff who need one system instead of five', 'More stable execution across front desk, clinical, billing, and leadership teams'],
      },
    ],
    related: ['why-elen1', 'features', 'pricing', 'book-demo'],
  },
  'resources': {
    path: '/resources',
    navGroup: 'resources',
    eyebrow: 'Resources',
    title: 'Resources for teams learning, evaluating, and rolling out elen1.',
    subtitle: 'Use this library to understand the workflows, rollout decisions, support paths, and technical references behind the platform.',
    highlights: ['Practical rollout and support content', 'Guidance for operators, clinicians, and technical teams', 'Documentation that helps teams self-serve earlier'],
    sections: [
      {
        title: 'Give teams practical content before they need a live call',
        body: 'Public resources should help prospects and new customers understand how elen1 fits into daily work before a full rollout or support conversation even starts. The best resource hub shortens the path from curiosity to confidence.',
        bullets: ['Setup and workflow guidance people can actually use', 'Integration and API direction for technical evaluators', 'Security and governance material that helps answer trust questions earlier'],
      },
      {
        title: 'Organize the content around the questions each team really has',
        body: 'Operators, clinicians, admins, and developers rarely come to the product with the same questions. Resource pages should make those paths clearer so teams can get to the right information without digging through generic content.',
        bullets: ['Guidance organized by need, not just by document type', 'A simpler handoff from evaluation into onboarding and rollout', 'Stronger self-serve understanding before demo and implementation calls'],
      },
    ],
    related: ['help-center', 'developer-api', 'guides', 'security'],
  },
  'help-center': {
    path: '/help-center',
    navGroup: 'resources',
    eyebrow: 'Resources',
    title: 'Help center',
    subtitle: 'Step-by-step help for setup, daily workflows, billing, patient communication, and operations.',
    highlights: ['Operator workflows', 'Staff onboarding material', 'Self-serve support content'],
    sections: [
      {
        title: 'Reduce repetitive support questions',
        body: 'A strong help center gives teams fast answers to the work they are trying to do right now, so onboarding and support are not entirely dependent on live sessions or internal workarounds.',
        bullets: ['Task-oriented articles tied to real clinic workflows', 'Screens and process explanations that help staff move faster', 'Support content useful for both administrators and day-to-day users'],
      },
      {
        title: 'Make documentation useful during implementation',
        body: 'The best documentation mirrors the way clinics actually learn software: by task, by role, and by the exceptions that come up in daily work. That makes it useful during rollout instead of only useful after the fact.',
        bullets: ['Step-by-step operational references for the tasks teams repeat every day', 'Role-specific guidance that makes onboarding less confusing', 'A faster ramp for new staff joining a live clinic environment'],
      },
    ],
    related: ['resources', 'practice-managers', 'mental-health-therapy', 'book-demo'],
  },
  'developer-api': {
    path: '/developer-api',
    navGroup: 'resources',
    eyebrow: 'Resources',
    title: 'Developer API',
    subtitle: 'Technical guidance for integrations, connectivity, and extension work around elen1.',
    highlights: ['Integration planning', 'Developer guidance', 'Security-aware connectivity'],
    sections: [
      {
        title: 'Support integrations without guesswork',
        body: 'Technical teams need clear access patterns, environment expectations, and data-flow guidance if they are going to evaluate integrations seriously. The API content should reduce guesswork instead of sending people into a sales conversation for basic technical context.',
        bullets: ['Developer-facing reference material that answers first-pass integration questions', 'Clear expectations for where and how integrations connect', 'Support for secure, well-governed extension work from the start'],
      },
      {
        title: 'Keep technical enablement aligned to governance',
        body: 'Public API information should help people understand not just that integrations are possible, but how they fit into the broader clinic operating model and security posture. That makes the handoff from technical evaluation into implementation much cleaner.',
        bullets: ['Practical framing for how integrations fit into the real operating workflow', 'Better clarity on governance and operational context', 'A cleaner handoff from technical review into implementation planning'],
      },
    ],
    related: ['resources', 'security', 'comparisons', 'book-demo'],
  },
  'guides': {
    path: '/guides',
    navGroup: 'resources',
    eyebrow: 'Resources',
    title: 'Guides',
    subtitle: 'Longer-form guidance for clinic operations, patient engagement, AI adoption, and growth planning.',
    highlights: ['Operational playbooks', 'Growth and retention guidance', 'AI workflow adoption support'],
    sections: [
      {
        title: 'Publish guidance that helps buyers think clearly',
        body: 'Guides should help operators and clinicians think more clearly about the workflow changes they are considering, not just recap what the product already does. That means writing around real decisions, tradeoffs, and rollout questions.',
        bullets: ['Clinic operations playbooks people can actually learn from', 'Patient engagement strategies tied to real workflow decisions', 'AI governance and rollout guidance that helps teams avoid vague adoption plans'],
      },
      {
        title: 'Move beyond feature pages',
        body: 'Educational content is where teams work through the why behind a workflow change, not just the software surface. That kind of content helps people build internal alignment before they ask the organization to change tools or habits.',
        bullets: ['Narratives that map to real implementation and operating questions', 'Content that supports evaluation, internal buy-in, and rollout planning', 'Resources that stay useful after the sale instead of disappearing once the contract is signed'],
      },
    ],
    related: ['resources', 'marketing-growth', 'why-elen1', 'book-demo'],
  },
  'pricing': {
    path: '/pricing',
    navGroup: 'pricing',
    eyebrow: 'Pricing',
    title: 'Pricing that matches clinic complexity.',
    subtitle: 'Start with the workflows you need now, then expand into broader operational coverage as the practice grows.',
    highlights: ['Simple starting point', 'Scales by team and workflow depth', 'Implementation aligned to clinic needs'],
    sections: [
      {
        title: 'Keep the pricing story practical',
        body: 'Buyers should understand what they are paying for, what rollout looks like, and how the platform expands with them.',
        bullets: ['Fit for solo, growing, and multi-site teams', 'Clear implementation path', 'Workflow-based platform packaging'],
      },
      {
        title: 'Support value-based conversations',
        body: 'The real pricing discussion is about consolidation, operational clarity, and what better workflows are worth to the business.',
        bullets: ['Lower tool sprawl over time', 'Clearer alignment between product and outcomes', 'Stronger fit for long-term growth'],
      },
    ],
    related: ['features', 'solo-practices', 'clinic-owners', 'book-demo'],
  },
  'book-demo': {
    path: '/book-demo',
    navGroup: 'cta',
    eyebrow: 'Book a demo',
    title: 'See elen1 in the context of your clinic.',
    subtitle: 'Walk through the workflows that matter most to your team, from scheduling and charting to payments and reporting.',
    highlights: ['Role-specific walkthroughs', 'A demo tailored to your workflows', 'Implementation and pricing discussion'],
    sections: [
      {
        title: 'Use the demo to answer operational questions',
        body: 'The best demo is not a generic tour. It should show how your clinic will book, document, coordinate, collect, and report inside one platform.',
        bullets: ['Tailored to role, specialty, and growth stage', 'Focused on real workflows instead of feature dumping', 'Clear next steps after the session'],
      },
      {
        title: 'Plan rollout with the right people in the room',
        body: 'Clinical, operational, and leadership stakeholders usually want to see different things. A focused demo helps align those perspectives earlier.',
        bullets: ['Owner, manager, and practitioner viewpoints', 'Implementation and migration discussion', 'Stronger internal buying confidence'],
      },
    ],
    related: ['features', 'who-we-serve', 'pricing', 'security'],
  },
} as Record<LandingCatalogPageKey, LandingCatalogPage>;

export const LANDING_TOP_NAV: LandingTopNav[] = [
  {
    label: 'Features',
    key: 'features',
    groups: [
      {
        title: 'Care delivery',
        items: [
          { key: 'emr-ehr', icon: 'ph-notebook', label: 'AI EMR & EHR', description: 'Start visits with the full patient picture and finish notes with less backtracking.' },
          { key: 'clinical-ai', icon: 'ph-sparkle', label: 'Clinical AI', description: 'Use AI for summaries, triage, and follow-up support right inside the workflow.' },
        ],
      },
      {
        title: 'Patient journey',
        items: [
          { key: 'patient-portal', icon: 'ph-users-three', label: 'Patient Portal', description: 'Let patients handle forms, messages, appointments, and next steps in one place.' },
          { key: 'calendar-booking', icon: 'ph-calendar-check', label: 'Calendar & Booking', description: 'Keep schedules, rooms, reminders, waitlists, and telehealth working from one booking flow.' },
        ],
      },
      {
        title: 'Revenue',
        items: [
          { key: 'billing-payments', icon: 'ph-credit-card', label: 'Billing & Payments', description: 'Handle invoices, balances, deposits, packages, and memberships without extra systems.' },
          { key: 'marketing-growth', icon: 'ph-megaphone-simple', label: 'Marketing Growth', description: 'Turn recalls, reviews, referrals, and reactivation into a repeatable clinic workflow.' },
        ],
      },
      {
        title: 'Operations',
        items: [
          { key: 'multi-site-operations', icon: 'ph-buildings', label: 'Multi-site Operations', description: 'Run multiple locations with shared standards, permissions, and clearer oversight.' },
          { key: 'reports-insights', icon: 'ph-chart-line-up', label: 'Reports & Insights', description: 'See revenue, utilization, retention, and day-to-day performance in one place.' },
        ],
      },
    ],
    aside: {
      eyebrow: 'Platform overview',
      title: 'Explore the parts of elen1 clinics use every day.',
      body: 'Open any workflow to see how it fits the clinic day, who it helps most, and why teams stop relying on disconnected tools.',
      ctaLabel: 'View all features',
      ctaKey: 'features',
    },
  },
  {
    label: 'Solutions',
    key: 'who-we-serve',
    groups: [
      {
        title: 'Specialties',
        items: SOLUTIONS_SPECIALTY_GROUPS.map((group) => ({
          key: group.categoryKey,
          icon: group.icon,
          label: group.title,
          description: group.banner,
        })),
      },
    ],
  },
  {
    label: 'Pricing',
    key: 'pricing',
  },
  {
    label: 'Resources',
    key: 'resources',
    groups: [
      {
        title: 'Start here',
        items: [
          { key: 'resources', icon: 'ph-folders', label: 'Resource Hub', description: 'Start with the main library for evaluation, rollout, and support content.' },
          { key: 'help-center', icon: 'ph-book-open-text', label: 'Help Center', description: 'Step-by-step articles for setup, daily workflows, billing, and operations.' },
          { key: 'guides', icon: 'ph-file-text', label: 'Guides', description: 'Longer-form playbooks for operations, engagement, AI adoption, and growth.' },
        ],
      },
      {
        title: 'Technical & trust',
        items: [
          { key: 'developer-api', icon: 'ph-code', label: 'Developer API', description: 'Integration guidance, connectivity patterns, and technical planning context.' },
          { key: 'security', icon: 'ph-shield-check', label: 'Security', description: 'Data protection, access controls, auditability, and governance posture.' },
        ],
      },
    ],
    aside: {
      eyebrow: 'Resources',
      title: 'Find the material your team actually needs next.',
      body: 'Use the hub for rollout guidance, support content, technical references, and longer-form operational playbooks.',
      ctaLabel: 'Open resources',
      ctaKey: 'resources',
    },
  },
  {
    label: 'About Us',
    key: 'why-elen1',
  },
];

export const LANDING_SUBPAGE_KEYS = Object.keys(LANDING_CATALOG_PAGES) as LandingCatalogPageKey[];

export const LANDING_SUBPAGE_PATHS = LANDING_SUBPAGE_KEYS.map((key) => LANDING_CATALOG_PAGES[key].path);

export type LandingContentLocale = 'en' | 'es';

const SPANISH_PAGE_LABELS: Partial<Record<LandingCatalogPageKey, string>> = {
  features: 'funciones',
  'emr-ehr': 'EMR y EHR con IA',
  'clinical-ai': 'IA clínica',
  'patient-portal': 'portal del paciente',
  'calendar-booking': 'agenda y reservas',
  'billing-payments': 'facturación y pagos',
  'multi-site-operations': 'operaciones multi-sede',
  'reports-insights': 'reportes e indicadores',
  'marketing-growth': 'marketing y crecimiento',
  'who-we-serve': 'soluciones',
  'clinic-owners': 'propietarios de clínica',
  'practice-managers': 'gerentes de práctica',
  practitioners: 'profesionales clínicos',
  'solo-practices': 'consultas individuales',
  'growing-teams': 'equipos en crecimiento',
  'multi-location-groups': 'grupos multi-sede',
  'primary-care-urgent-care': 'atención primaria y urgencias',
  'aesthetics-wellness': 'estética y bienestar',
  'mental-health-therapy': 'salud mental y terapia',
  'wellness-preventive-care': 'bienestar y cuidado preventivo',
  'medical-aesthetics': 'estética médica',
  'womens-mens-health': 'salud femenina y masculina',
  'musculoskeletal-performance': 'musculoesquelético y rendimiento',
  'mental-health-lifestyle': 'salud mental y estilo de vida',
  medspa: 'medspa',
  'skin-clinic': 'clínicas de piel',
  'aesthetic-clinic': 'clínicas estéticas',
  'laser-clinic': 'clínicas láser',
  dermatology: 'dermatología',
  'hair-transplant-clinic': 'clínicas de trasplante capilar',
  'cosmetic-surgery': 'cirugía cosmética',
  spa: 'spa',
  fertility: 'fertilidad',
  'sexual-health': 'salud sexual',
  'ivf-gynecology': 'FIV y ginecología',
  'pelvic-health': 'salud pélvica',
  'mens-health': 'salud masculina',
  'hormone-replacement-therapy': 'terapia hormonal',
  'physical-therapy': 'fisioterapia',
  osteopathy: 'osteopatía',
  'sports-medicine': 'medicina deportiva',
  'regenerative-medicine': 'medicina regenerativa',
  chiropractic: 'quiropráctica',
  'prp-therapy': 'terapia PRP',
  'mental-health': 'salud mental',
  psychiatry: 'psiquiatría',
  psychology: 'psicología',
  coaching: 'coaching',
  'therapy-counseling': 'terapia y consejería',
  'speech-therapy': 'terapia del habla',
  'adhd-assessment': 'evaluación de TDAH',
  'occupational-therapy': 'terapia ocupacional',
  'practice-owner': 'propietarios de práctica',
  'treatment-providers': 'profesionales de tratamiento',
  'front-of-house-staff': 'equipo de recepción',
  marketers: 'equipos de marketing',
  enterprise: 'empresas',
  'large-practice': 'prácticas grandes',
  'small-practice': 'prácticas pequeñas',
  'solo-practitioner': 'profesionales independientes',
  'all-specialities': 'todas las especialidades',
  'why-elen1': 'por qué Elen1',
  'case-studies': 'casos de éxito',
  security: 'seguridad',
  comparisons: 'comparaciones',
  resources: 'recursos',
  'help-center': 'centro de ayuda',
  'developer-api': 'API para desarrolladores',
  guides: 'guías',
  pricing: 'precios',
  'book-demo': 'reservar una demo',
};

const SPANISH_TITLE_OVERRIDES: Partial<Record<LandingCatalogPageKey, string>> = {
  features: 'Funciones de Elen1 para una clínica conectada.',
  'who-we-serve': 'Soluciones para cada modelo de clínica.',
  'why-elen1': 'Por qué los equipos eligen Elen1.',
  resources: 'Recursos para evaluar, implementar y operar Elen1.',
  pricing: 'Precios adaptados a la complejidad de tu clínica.',
  'book-demo': 'Mira Elen1 en el contexto de tu clínica.',
};

const SPANISH_EYEBROWS: Record<LandingCatalogPage['navGroup'], string> = {
  features: 'Funciones',
  'who-we-serve': 'Soluciones',
  'why-us': 'Por qué Elen1',
  resources: 'Recursos',
  pricing: 'Precios',
  cta: 'Reservar una demo',
};

const SPANISH_COMMON_HIGHLIGHTS = {
  features: ['Flujos conectados', 'Menos trabajo administrativo', 'Visibilidad operativa'],
  'who-we-serve': ['Flujos por especialidad', 'Agenda, historia clínica, pagos y seguimiento conectados', 'Visibilidad para clínicas en crecimiento'],
  'why-us': ['Una sola capa operativa', 'Menos herramientas desconectadas', 'Mejor control del día clínico'],
  resources: ['Guías prácticas', 'Material de implementación', 'Soporte para evaluación'],
  pricing: ['Punto de partida simple', 'Escala por equipo y profundidad de flujo', 'Implementación alineada a la clínica'],
  cta: ['Demo por rol', 'Recorrido adaptado a tus flujos', 'Conversación de implementación y precios'],
} satisfies Record<LandingCatalogPage['navGroup'], string[]>;

function spanishLabelFor(key: LandingCatalogPageKey, fallback: string) {
  return SPANISH_PAGE_LABELS[key] ?? fallback.toLowerCase();
}

function spanishTitleFor(key: LandingCatalogPageKey, page: LandingCatalogPage) {
  if (SPANISH_TITLE_OVERRIDES[key]) return SPANISH_TITLE_OVERRIDES[key]!;
  const label = spanishLabelFor(key, page.title);
  if (page.navGroup === 'features') return `Elen1 para ${label}.`;
  if (page.navGroup === 'who-we-serve') return `Elen1 para ${label}.`;
  if (page.navGroup === 'resources') return `${label.charAt(0).toUpperCase()}${label.slice(1)} para equipos clínicos.`;
  return page.title;
}

function spanishSubtitleFor(key: LandingCatalogPageKey, page: LandingCatalogPage) {
  const label = spanishLabelFor(key, page.title);
  if (page.navGroup === 'features') {
    return `Gestiona ${label} con agenda, pacientes, documentación, pagos, comunicación y reportes trabajando en un mismo sistema.`;
  }
  if (page.navGroup === 'who-we-serve') {
    return `Elen1 ayuda a ${label} a coordinar reservas, admisión, atención, pagos, seguimiento y reportes sin depender de herramientas desconectadas.`;
  }
  if (page.navGroup === 'why-us') {
    return 'Una plataforma pensada para reducir fricción operativa, conectar el trabajo clínico y dar más claridad a propietarios, gerentes y equipos de atención.';
  }
  if (page.navGroup === 'resources') {
    return 'Contenido práctico para evaluar la plataforma, preparar la implementación, formar al equipo y mejorar los flujos diarios de la clínica.';
  }
  if (page.navGroup === 'pricing') {
    return 'Empieza con los flujos que necesitas ahora y expande la cobertura operativa a medida que la práctica crece.';
  }
  return 'Agenda una conversación para ver cómo Elen1 se adapta a tu clínica, tu equipo y tus flujos de trabajo.';
}

function spanishSectionsFor(key: LandingCatalogPageKey, page: LandingCatalogPage): LandingCatalogSection[] {
  const label = spanishLabelFor(key, page.title);
  if (page.navGroup === 'features') {
    return [
      {
        title: `Coordina ${label} sin cambiar de sistema`,
        body: 'Elen1 conecta la información clínica, operativa y financiera para que cada equipo pueda avanzar con el contexto correcto.',
        bullets: ['Mantén agenda, pacientes y tareas conectadas', 'Reduce pasos manuales entre roles', 'Da visibilidad clara a responsables clínicos y operativos'],
      },
      {
        title: 'Convierte el flujo diario en un proceso repetible',
        body: 'La plataforma ayuda a estandarizar el trabajo sin quitar flexibilidad a la forma en que cada clínica atiende a sus pacientes.',
        bullets: ['Plantillas y vistas por rol', 'Seguimiento de pendientes y próximos pasos', 'Reportes vinculados a la actividad real'],
      },
    ];
  }
  if (page.navGroup === 'who-we-serve') {
    return [
      {
        title: `Flujos diseñados para ${label}`,
        body: 'El equipo puede gestionar reservas, formularios, notas, pagos, seguimiento y contexto del paciente desde un espacio compartido.',
        bullets: ['Conecta citas, pacientes, cobros y comunicación', 'Da a recepción, profesionales y gerentes una vista común', 'Reduce duplicación administrativa entre admisión, atención y seguimiento'],
      },
      {
        title: 'Crece con operaciones más claras',
        body: 'A medida que aumentan el volumen, los servicios o las sedes, Elen1 ayuda a ordenar el trabajo diario y mantener control operativo.',
        bullets: ['Mide demanda, utilización, ingresos y seguimiento', 'Usa flujos por rol para que cada equipo vea lo importante', 'Facilita una implementación más clara para este tipo de clínica'],
      },
    ];
  }
  if (page.navGroup === 'resources') {
    return [
      {
        title: 'Encuentra respuestas sin depender siempre de soporte en vivo',
        body: 'Los recursos están pensados para ayudar a compradores, operadores, clínicos y equipos técnicos a entender los flujos antes y durante la implementación.',
        bullets: ['Material organizado por necesidad real', 'Guías para evaluación, adopción y soporte', 'Contenido útil para equipos clínicos y administrativos'],
      },
      {
        title: 'Alinea evaluación, implementación y operación',
        body: 'La documentación debe acompañar decisiones reales: qué cambiar, cómo capacitar al equipo y cómo medir si el nuevo flujo está funcionando.',
        bullets: ['Referencias paso a paso', 'Mejor preparación antes de la demo', 'Apoyo continuo después del lanzamiento'],
      },
    ];
  }
  return page.sections.map((section, index) => ({
    title: index === 0 ? 'Opera con más claridad desde el primer día' : 'Escala sin añadir complejidad innecesaria',
    body: index === 0
      ? 'Elen1 reúne atención, administración, pagos y seguimiento para que la clínica pueda trabajar con menos fricción.'
      : 'La plataforma se adapta a equipos pequeños, prácticas en crecimiento y organizaciones con varias sedes.',
    bullets: section.bullets.map((_, bulletIndex) => [
      'Reduce herramientas desconectadas y pasos duplicados',
      'Mejora la visibilidad entre roles y sedes',
      'Conecta actividad clínica, ingresos y experiencia del paciente',
    ][bulletIndex % 3]),
  }));
}

function spanishCapabilitiesFor(page: LandingCatalogPage): LandingCatalogCapability[] | undefined {
  if (!page.capabilities?.length) return page.capabilities;
  return [
    {
      title: 'Flujo del paciente más claro',
      body: 'Mantén reservas, admisión, estado de visita, documentación, pago y próximos pasos conectados durante todo el recorrido.',
    },
    {
      title: 'Menos carga operativa',
      body: 'Reduce hojas de cálculo, doble captura y traspasos desconectados entre recepción, profesionales, facturación y gerencia.',
    },
    {
      title: 'Mejor visibilidad',
      body: 'Observa rendimiento, carga de trabajo y señales de seguimiento en un solo sistema para actuar antes.',
    },
  ];
}

function spanishCtaFor(key: LandingCatalogPageKey, page: LandingCatalogPage): LandingCatalogCta | undefined {
  if (!page.cta) return page.cta;
  const label = spanishLabelFor(key, page.title);
  return {
    eyebrow: SPANISH_EYEBROWS[page.navGroup],
    title: `Mira cómo Elen1 encaja con ${label}.`,
    body: 'Agenda un recorrido enfocado y mapearemos la plataforma a tus servicios, estructura de equipo, recorrido del paciente y necesidades de reporte.',
    primaryLabel: 'Reservar una demo',
    secondaryLabel: 'Explorar funciones',
    proofs: [
      'Agenda, admisión, historia clínica, pagos y seguimiento en un solo flujo',
      'Vistas por rol para propietarios, gerentes, profesionales y recepción',
      'Reportes que conectan pacientes, ingresos y ejecución del equipo',
    ],
  };
}

function spanishFaqFor(page: LandingCatalogPage): LandingCatalogFaqItem[] | undefined {
  if (!page.faq?.length) return page.faq;
  return [
    {
      question: '¿Qué cubre esta página?',
      answer: page.navGroup === 'who-we-serve'
        ? 'Muestra cómo Elen1 puede adaptarse a este tipo de clínica, rol o modelo operativo.'
        : 'Resume cómo este flujo encaja dentro de una plataforma clínica conectada.',
    },
    {
      question: '¿Qué puede mejorar el equipo con Elen1?',
      answer: 'Puede reducir trabajo manual, conectar información dispersa y dar más visibilidad sobre pacientes, ingresos y tareas pendientes.',
    },
    {
      question: '¿Para quién es más relevante?',
      answer: 'Para propietarios, gerentes, profesionales y equipos operativos que quieren reemplazar herramientas desconectadas por un flujo más claro.',
    },
    {
      question: '¿Cómo encaja con el resto de la plataforma?',
      answer: 'Elen1 conecta este flujo con agenda, coordinación de pacientes, pagos, reportes y gobierno operativo.',
    },
  ];
}

function localizeCatalogPage(key: LandingCatalogPageKey, locale: LandingContentLocale): LandingCatalogPage {
  const page = LANDING_CATALOG_PAGES[key];
  if (locale !== 'es') return page;
  return {
    ...page,
    eyebrow: SPANISH_EYEBROWS[page.navGroup],
    title: spanishTitleFor(key, page),
    subtitle: spanishSubtitleFor(key, page),
    highlights: SPANISH_COMMON_HIGHLIGHTS[page.navGroup],
    sections: spanishSectionsFor(key, page),
    capabilities: spanishCapabilitiesFor(page),
    screenshots: page.screenshots?.map((screenshot, index) => ({
      ...screenshot,
      title: index === 0 ? 'Flujo conectado en Elen1' : 'Vista operativa del producto',
      body: 'Una vista práctica del trabajo diario para que el equipo pueda coordinarse con más contexto y menos herramientas separadas.',
      alt: `Vista de Elen1 para ${spanishLabelFor(key, page.title)}`,
    })),
    cta: spanishCtaFor(key, page),
    faq: spanishFaqFor(page),
    relatedDescriptions: undefined,
  };
}

/* Accepts both full menu cards and the label-only links used in the solutions
   side lists, so localization keeps whichever shape it was given. */
function localizeMenuCard<T extends { key: LandingCatalogPageKey; label: string }>(card: T, locale: LandingContentLocale): T {
  if (locale !== 'es') return card;
  const page = localizeCatalogPage(card.key, 'es');
  const label = SPANISH_PAGE_LABELS[card.key] ? page.title.replace(/^Elen1 para /, '').replace(/\.$/, '') : card.label;
  if ('description' in card) {
    return { ...card, label, description: page.subtitle };
  }
  return { ...card, label };
}

export function getLocalizedSolutionsSpecialtyGroups(locale: LandingContentLocale) {
  if (locale !== 'es') return SOLUTIONS_SPECIALTY_GROUPS;
  return SOLUTIONS_SPECIALTY_GROUPS.map((group) => ({
    ...group,
    title: spanishLabelFor(group.categoryKey, group.title).replace(/^\w/, (letter) => letter.toUpperCase()),
    banner: spanishSubtitleFor(group.categoryKey, LANDING_CATALOG_PAGES[group.categoryKey]),
    items: group.items.map((item) => localizeMenuCard(item, 'es')),
  }));
}

export function getLocalizedSolutionsRoleItems(locale: LandingContentLocale) {
  return locale === 'es' ? SOLUTIONS_ROLE_ITEMS.map((item) => localizeMenuCard(item, 'es')) : SOLUTIONS_ROLE_ITEMS;
}

export function getLocalizedSolutionsBusinessSizeItems(locale: LandingContentLocale) {
  return locale === 'es' ? SOLUTIONS_BUSINESS_SIZE_ITEMS.map((item) => localizeMenuCard(item, 'es')) : SOLUTIONS_BUSINESS_SIZE_ITEMS;
}

export function getLocalizedSolutionsOtherItems(locale: LandingContentLocale) {
  return locale === 'es' ? SOLUTIONS_OTHER_ITEMS.map((item) => localizeMenuCard(item, 'es')) : SOLUTIONS_OTHER_ITEMS;
}

export function getLandingTopNav(locale: LandingContentLocale = 'en'): LandingTopNav[] {
  if (locale !== 'es') return LANDING_TOP_NAV;
  return LANDING_TOP_NAV.map((item) => ({
    ...item,
    label: ({
      features: 'Funciones',
      'who-we-serve': 'Soluciones',
      pricing: 'Precios',
      resources: 'Recursos',
      'why-elen1': 'Sobre nosotros',
    } as Partial<Record<LandingCatalogPageKey, string>>)[item.key] ?? item.label,
    groups: item.groups?.map((group) => ({
      ...group,
      title: ({
        'Care delivery': 'Atención clínica',
        'Patient journey': 'Recorrido del paciente',
        Revenue: 'Ingresos',
        Operations: 'Operaciones',
        Specialties: 'Especialidades',
        'Start here': 'Empieza aquí',
        'Technical & trust': 'Técnico y confianza',
      } as Record<string, string>)[group.title] ?? group.title,
      items: group.items.map((entry) => localizeMenuCard(entry, 'es')),
    })),
    aside: item.aside
      ? {
        ...item.aside,
        eyebrow: item.key === 'resources' ? 'Recursos' : 'Resumen de plataforma',
        title: item.key === 'resources'
          ? 'Encuentra el material que tu equipo necesita ahora.'
          : 'Explora los flujos de Elen1 que las clínicas usan todos los días.',
        body: item.key === 'resources'
          ? 'Usa el centro de recursos para guías de implementación, soporte, referencias técnicas y playbooks operativos.'
          : 'Abre cualquier flujo para ver cómo encaja en el día clínico, a quién ayuda y por qué reduce herramientas desconectadas.',
        ctaLabel: item.key === 'resources' ? 'Abrir recursos' : 'Ver todas las funciones',
      }
      : item.aside,
  }));
}

export function getLandingCatalogPage(key: LandingCatalogPageKey, locale: LandingContentLocale = 'en') {
  return localizeCatalogPage(key, locale);
}

export function getLandingCatalogPageByPath(path: string) {
  return LANDING_SUBPAGE_KEYS.find((key) => LANDING_CATALOG_PAGES[key].path === path) ?? null;
}
